import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { useCardPayment } from '../lib/cardTerminal';
import { TipInput, bookTip } from '../components/TipInput';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, addDays, parseISO, isValid as isValidDate } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import BookingSharesPanel from '../components/BookingSharesPanel';
import { MatchPlayersPaid } from '../components/MatchPlayersPaid';
import { AdultAcademyPanel } from '../components/AdultAcademyPanel';
import { AcademyJuniorChips } from '../components/AcademyJuniorChips';
import { 
  CaretLeft, CaretRight, CalendarBlank, X, Barbell, PencilSimple, Trash, Clock, MapPin, ArrowsClockwise, Receipt, GearSix, CheckCircle, Wrench, NotePencil
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { AboSessionBadges } from '../components/AboSessionBadges';
import { TrainingPrepaidPay } from '../components/TrainingPrepaidPay';
import { OpenMatchFromBooking } from '../components/OpenMatchFromBooking';

// Safely parse a "YYYY-MM-DD" string. Returns Date on success, null on failure.
const parseISOSafe = (s) => {
  try {
    const d = parseISO(s);
    return isValidDate(d) ? d : null;
  } catch (_) { return null; }
};

const AdminBookings = () => {
  const { user, t, language, API } = useAuth();
  const payByCard = useCardPayment();
  // Card → physical terminal first (if paired); returns false when the customer's card payment didn't go through.
  const chargeCardIfNeeded = async (method, amount, ref) => {
    if (method !== 'card') return true;
    const term = await payByCard(amount, ref);
    if (!term.ok) toast.error(`${L('Card payment not completed', 'Kartenzahlung nicht abgeschlossen')}: ${term.reason}`, { duration: 10000 });
    return term.ok;
  };
  const navigate = useNavigate();
  const dateLocale = language === 'de' ? de : enUS;
  const L = (en, deu) => language === 'de' ? deu : en;
  const isTrainer = user?.role === 'trainer';
  const trainerLocked = (b) => isTrainer && b.booking_type !== 'training' && b.user_id !== user.id && (
    !user.can_block_trainer_time
    || ['paid', 'partial', 'refunded'].includes(b.payment_status) || !!b.use_credits
    || !!b.paid_via_subscription_id || (b.voucher_deducted || 0) > 0 || (b.shares || []).some(x => x.is_paid));
  
  // Read ?date=YYYY-MM-DD from URL synchronously so first render already
  // shows the correct day (avoids the "today flash → jump" race).
  const initialDate = (() => {
    try {
      const raw = new URLSearchParams(window.location.search).get('date');
      if (raw) {
        const parsed = parseISOSafe(raw);
        if (parsed) return parsed;
      }
    } catch (_) { /* ignore */ }
    return new Date();
  })();
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [searchParams, setSearchParams] = useSearchParams();
  const [courts, setCourts] = useState([]);
  // Full list of ALL courts (padel + golf) — used to split the "Court hours"
  // KPI in the header so admins see padel and golf totals separately even
  // while a single tab is active. Loaded once because the set of courts
  // changes rarely.
  const [allCourts, setAllCourts] = useState([]);
  const [courtType, setCourtType] = useState('padel'); // 'padel' | 'golf'

  // Keep the current calendar date in the URL (?date=YYYY-MM-DD) so a hard
  // refresh restores the same day.
  useEffect(() => {
    const iso = format(selectedDate, 'yyyy-MM-dd');
    if (searchParams.get('date') !== iso) {
      const next = new URLSearchParams(searchParams);
      next.set('date', iso);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // When switching tabs, force booking type back to "regular" since golf
  // simulators don't have trainer-led lessons.
  React.useEffect(() => {
    if (courtType === 'golf') {
      setBookingType('regular');
      setLessonPrice('');
    }
  }, [courtType]);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  // Track whether the initial fetch is still pending so background 20-sec
  // refetches never replace the grid with the "Loading..." placeholder —
  // doing so would unmount the booking grid and reset the user's scroll
  // position to the top (reported Oct 2026 after deploy).
  const [initialLoaded, setInitialLoaded] = useState(false);
  
  // Customer search
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  // Create booking modal
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingDuration, setBookingDuration] = useState('60');
  const [showCustomDuration, setShowCustomDuration] = useState(false);
  const [customEndTime, setCustomEndTime] = useState('');
  const [editingCourt, setEditingCourt] = useState(null);
  const [courtEditForm, setCourtEditForm] = useState({ name: '', description: '', is_indoor: true });
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [bookingNotes, setBookingNotes] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingType, setBookingType] = useState('regular');
  const [selectedTrainer, setSelectedTrainer] = useState('');
  const [lessonPrice, setLessonPrice] = useState('');
  const [pricePerPerson, setPricePerPerson] = useState('');
  const [participants, setParticipants] = useState('1');
  // Auto-pricing for training bookings (matches the trainer-schedule modal).
  // We sync pricePerPerson to the official matrix when bookingType=training,
  // unless the user has explicitly typed a non-matrix value (override).
  const [trainingAutoPrice, setTrainingAutoPrice] = useState(null); // {price_per_person, tier}
  const [trainingPriceOverride, setTrainingPriceOverride] = useState(false);
  const [customerSubs, setCustomerSubs] = useState([]);
  const [customerVouchers, setCustomerVouchers] = useState([]);
  const [selectedVoucher, setSelectedVoucher] = useState('');
  const [autoUseSubId, setAutoUseSubId] = useState('');
  const [customPrice, setCustomPrice] = useState('');  // admin/staff/trainer override for regular court bookings (legacy voucher rate etc.)
  const [multiCourt, setMultiCourt] = useState(false);
  const [selectedCourts, setSelectedCourts] = useState([]);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringEndDate, setRecurringEndDate] = useState('');

  // Detail / Edit booking
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [openMatchFor, setOpenMatchFor] = useState(null);
  const [detailBooking, setDetailBooking] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [showDeleteOptions, setShowDeleteOptions] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [payMethodOpen, setPayMethodOpen] = useState(false);
  const [payParticipantName, setPayParticipantName] = useState('');
  const [payCount, setPayCount] = useState(0);
  const [trainingTip, setTrainingTip] = useState('');
  useEffect(() => { setPayCount(0); setTrainingTip(''); }, [detailBooking?.id]);
  const [showFullPayMethod, setShowFullPayMethod] = useState(false);
  // Drag & drop state
  const [draggedBooking, setDraggedBooking] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // { courtId, time }

  // Special days overrides
  const [showOverrides, setShowOverrides] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [ovForm, setOvForm] = useState({ date: format(new Date(), 'yyyy-MM-dd'), is_closed: false, opening_time: '08:00', closing_time: '18:00', note: '' });
  // Bulk close range (renovations, seasonal closures)
  const [rangeForm, setRangeForm] = useState({ start_date: format(new Date(), 'yyyy-MM-dd'), end_date: '', note: '' });
  const [rangeSaving, setRangeSaving] = useState(false);

  // Editable colour-legend labels (loaded from /booking-legend)
  const [legendEntries, setLegendEntries] = useState([]);
  const [showLegendEditor, setShowLegendEditor] = useState(false);
  const [legendDraft, setLegendDraft] = useState([]);
  const [legendSaving, setLegendSaving] = useState(false);

  const loadLegend = useCallback(async () => {
    try {
      const { data } = await API.get('/booking-legend');
      setLegendEntries(data);
    } catch (e) { /* silent */ }
  }, [API]);

  React.useEffect(() => { loadLegend(); }, [loadLegend]);

  // Tailwind classes for every colour key (must match backend DEFAULT_LEGEND).
  // Kept as a static map because Tailwind's JIT needs literal class strings.
  //
  // Automatic-only palette — the calendar chooses the colour based on booking
  // type + payment status. No manual override picker.
  //   court_paid       Blue         Court, fully paid online
  //   court_unpaid     Red          Court, unpaid (incl. "Fix courts" like
  //                                 corporate/tournament/maintenance — same red)
  // Colour palette (updated Feb 2026):
  //   court_paid       Light blue    Court paid online (Datatrans)
  //   court_unpaid     Red           Court unpaid / Fix
  //   court_voucher    Light green   Court paid via 10x pass OR regular subscription
  //   training_paid    Dark blue     Training paid (Abo, Voucher, or cash-collected)
  //   training_unpaid  Dark green    Training unpaid or partially paid
  //   training_junior  Gray          Junior Academy training
  //   training_adult   Yellow        Adult Academy sessions (season-fee covered)
  const COLOR_STYLES = {
    court_paid:      { dot: 'bg-sky-400 border-sky-600',             bg: 'bg-sky-400',        border: 'border-sky-600' },
    court_unpaid:    { dot: 'bg-red-500 border-red-700',             bg: 'bg-red-500',        border: 'border-red-700' },
    court_voucher:   { dot: 'bg-emerald-300 border-emerald-500',     bg: 'bg-emerald-300',    border: 'border-emerald-500' },
    training_paid:   { dot: 'bg-blue-700 border-blue-900',           bg: 'bg-blue-700',       border: 'border-blue-900' },
    training_unpaid: { dot: 'bg-emerald-600 border-emerald-800',     bg: 'bg-emerald-600',    border: 'border-emerald-800' },
    training_junior: { dot: 'bg-slate-400 border-slate-600',         bg: 'bg-slate-400',      border: 'border-slate-600' },
    training_adult:  { dot: 'bg-yellow-400 border-yellow-600',       bg: 'bg-yellow-400',     border: 'border-yellow-600' },
  };
  const labelFor = (entry) => (language === 'de' ? entry.label_de : entry.label_en);

  const loadOverrides = useCallback(async () => {
    try {
      const { data } = await API.get('/business-hours/overrides');
      setOverrides(data);
    } catch (e) { /* silent */ }
  }, [API]);

  React.useEffect(() => { loadOverrides(); }, [loadOverrides]);

  // Data repair (price audit + repair)
  const [showRepair, setShowRepair] = useState(false);
  const [repairAudit, setRepairAudit] = useState(null);
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairIncludePaid, setRepairIncludePaid] = useState(false);
  const loadAudit = useCallback(async (includePaid) => {
    setRepairLoading(true);
    try {
      const { data } = await API.get(`/admin/booking-price-audit?include_paid=${includePaid ? 'true' : 'false'}`);
      setRepairAudit(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    } finally { setRepairLoading(false); }
  }, [API, L]);
  const runRepair = useCallback(async () => {
    if (!window.confirm(L(
      `Repair ${repairAudit?.mismatches || 0} booking(s)? This updates the amount field.`,
      `${repairAudit?.mismatches || 0} Buchung(en) reparieren? Dies aktualisiert den Betrag.`
    ))) return;
    setRepairLoading(true);
    try {
      const { data } = await API.post(`/admin/booking-price-repair?include_paid=${repairIncludePaid ? 'true' : 'false'}`);
      toast.success(L(
        `Repaired ${data.updated} bookings (+${data.total_revenue_diff} CHF)`,
        `${data.updated} Buchungen repariert (+${data.total_revenue_diff} CHF)`
      ));
      await loadAudit(repairIncludePaid);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    } finally { setRepairLoading(false); }
  }, [API, L, repairAudit, repairIncludePaid, loadAudit]);

  const saveOverride = async () => {
    if (!ovForm.date) { toast.error(L('Pick a date', 'Datum wählen')); return; }
    try {
      const payload = {
        date: ovForm.date,
        is_closed: ovForm.is_closed,
        opening_time: ovForm.is_closed ? null : ovForm.opening_time,
        closing_time: ovForm.is_closed ? null : ovForm.closing_time,
        note: ovForm.note || null,
      };
      await API.post('/business-hours/overrides', payload);
      toast.success(L('Saved', 'Gespeichert'));
      setOvForm(f => ({ ...f, note: '' }));
      loadOverrides();
      // Reload current view if it matches
      if (ovForm.date === format(selectedDate, 'yyyy-MM-dd')) loadBookings(selectedDate);
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  const deleteOverride = async (date) => {
    if (!window.confirm(L(`Remove special hours for ${date}?`, `Sondertag ${date} entfernen?`))) return;
    try {
      await API.delete(`/business-hours/overrides/${date}`);
      toast.success(L('Removed', 'Entfernt'));
      loadOverrides();
      if (date === format(selectedDate, 'yyyy-MM-dd')) loadBookings(selectedDate);
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  const closeDateRange = async () => {
    if (!rangeForm.start_date || !rangeForm.end_date) {
      toast.error(L('Pick both start and end date', 'Start- und End-Datum wählen')); return;
    }
    if (rangeForm.end_date < rangeForm.start_date) {
      toast.error(L('End date must be after start date', 'End-Datum muss nach Start-Datum liegen')); return;
    }
    const days = Math.floor((new Date(rangeForm.end_date) - new Date(rangeForm.start_date)) / 86400000) + 1;
    if (!window.confirm(L(
      `Close the club for ${days} day(s)? This blocks ALL customer bookings from ${rangeForm.start_date} to ${rangeForm.end_date}.`,
      `Club für ${days} Tag(e) schliessen? Alle Kundenbuchungen von ${rangeForm.start_date} bis ${rangeForm.end_date} werden geblockt.`
    ))) return;
    setRangeSaving(true);
    try {
      const { data } = await API.post('/business-hours/close-range', {
        start_date: rangeForm.start_date,
        end_date: rangeForm.end_date,
        note: rangeForm.note || null,
      });
      toast.success(L(`Closed ${data.days} day(s)`, `${data.days} Tag(e) geschlossen`));
      setRangeForm(f => ({ ...f, note: '' }));
      loadOverrides();
      loadBookings(selectedDate);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    } finally { setRangeSaving(false); }
  };

  const openDateRange = async () => {
    if (!rangeForm.start_date || !rangeForm.end_date) {
      toast.error(L('Pick both start and end date', 'Start- und End-Datum wählen')); return;
    }
    if (!window.confirm(L(
      `Re-open all days between ${rangeForm.start_date} and ${rangeForm.end_date}?`,
      `Alle Tage zwischen ${rangeForm.start_date} und ${rangeForm.end_date} wieder öffnen?`
    ))) return;
    setRangeSaving(true);
    try {
      const { data } = await API.post('/business-hours/open-range', {
        start_date: rangeForm.start_date,
        end_date: rangeForm.end_date,
      });
      toast.success(L(`Re-opened ${data.days} day(s)`, `${data.days} Tag(e) wieder geöffnet`));
      loadOverrides();
      loadBookings(selectedDate);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    } finally { setRangeSaving(false); }
  };
  // Resize state
  const [resizing, setResizing] = useState(null); // { booking, startY, originalDuration, currentDuration }

  const SLOT_PX = 29; // height of each 30-min slot

  React.useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const deltaY = e.clientY - resizing.startY;
      const stepCount = Math.round(deltaY / SLOT_PX); // each step = 30 min
      const newDuration = Math.max(30, Math.min(240, resizing.originalDuration + stepCount * 30));
      if (newDuration !== resizing.currentDuration) {
        setResizing(prev => prev ? { ...prev, currentDuration: newDuration } : null);
      }
    };
    const onUp = async () => {
      const r = resizing;
      setResizing(null);
      if (!r || r.currentDuration === r.originalDuration) return;
      try {
        await API.put(`/bookings/${r.booking.id}`, { duration_minutes: r.currentDuration });
        toast.success(L(`Duration updated to ${r.currentDuration} min`, `Dauer auf ${r.currentDuration} min angepasst`));
        loadBookings(selectedDate);
      } catch (e) {
        toast.error(e.response?.data?.detail || L('Resize failed (conflict?)', 'Anpassen fehlgeschlagen (Konflikt?)'));
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizing]);

  const handleDropOnSlot = async (court, time) => {
    if (!draggedBooking) return;
    setDropTarget(null);
    // No-op if dropped on its own start
    if (draggedBooking.court_id === court.id && draggedBooking.start_time === time) {
      setDraggedBooking(null);
      return;
    }
    try {
      await API.put(`/bookings/${draggedBooking.id}`, {
        court_id: court.id,
        start_time: time,
      });
      toast.success(L('Booking moved', 'Buchung verschoben'));
      loadBookings(selectedDate);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Move failed (slot conflict?)', 'Verschieben fehlgeschlagen (Konflikt?)'));
    } finally {
      setDraggedBooking(null);
    }
  };

  const getTimeSlots = () => {
    if (!pricingInfo) return [];
    const openHour = parseInt(pricingInfo.opening_time.split(':')[0]);
    const closeHour = pricingInfo.closing_time === '00:00' ? 24 : parseInt(pricingInfo.closing_time.split(':')[0]);
    const slots = [];
    for (let h = openHour; h < closeHour; h++) {
      for (let m = 0; m < 60; m += 30) {
        slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      }
    }
    return slots;
  };
  const timeSlots = getTimeSlots();

  useEffect(() => { loadCourts(); loadTrainers(); }, [courtType]);
  useEffect(() => {
    if (selectedDate) { loadBookings(selectedDate); loadPricing(selectedDate); }
  }, [selectedDate, courtType]);

  // Live-sync the calendar for multi-station use (Admin at desk + Empfang at
  // counter). Without this, when Admin marks a court as paid the tile turns
  // blue on THEIR screen, but Empfang keeps seeing the red tile because their
  // React state was never refetched. We poll every 20 s while the tab is
  // visible, plus refetch immediately on focus/visibility change so switching
  // back to the tab shows the current state instantly.
  useEffect(() => {
    if (!selectedDate) return;
    let cancelled = false;
    const refetch = () => {
      if (!cancelled && document.visibilityState === 'visible') {
        loadBookings(selectedDate);
      }
    };
    const iv = setInterval(refetch, 20000);
    const onFocus = () => refetch();
    const onVis = () => { if (document.visibilityState === 'visible') refetch(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Auto-fetch training matrix price when bookingType=training and inputs change.
  useEffect(() => {
    if (bookingType !== 'training' || !showBookingModal || trainingPriceOverride) {
      if (bookingType !== 'training') setTrainingAutoPrice(null);
      return;
    }
    const slot = selectedSlot;
    if (!slot) return;
    const dur = parseInt(bookingDuration);
    const parts = Math.max(1, parseInt(participants) || 1);
    if (![60, 90, 120].includes(dur) || parts < 1 || parts > 4) {
      setTrainingAutoPrice(null);
      return;
    }
    const dateStr = format(slot.date, 'yyyy-MM-dd');
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get(
          `/pricing/training?date=${dateStr}&start_time=${slot.time}&duration_minutes=${dur}&participants=${parts}`
        );
        if (cancelled) return;
        setTrainingAutoPrice(data);
        // Override flag is the single source of truth — manual edits flip it
        // to true (see input onChange). Otherwise always sync to the official
        // matrix price so the bottom Total matches the top hint.
        setPricePerPerson(String(data.price_per_person));
      } catch (e) {
        if (!cancelled) setTrainingAutoPrice(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingType, showBookingModal, selectedSlot, bookingDuration, participants, trainingPriceOverride]);

  useEffect(() => {
    // Load active subscriptions AND vouchers for the customer (for any booking type)
    if (selectedCustomer) {
      (async () => {
        try {
          const { data } = await API.get(`/subscriptions?user_id=${selectedCustomer}&active_only=true`);
          const list = (Array.isArray(data) ? data : []).filter(s => (s.lessons_remaining || 0) > 0);
          const wantedType = bookingType === 'training' ? 'training' : 'court';
          const matching = list.filter(s => s.type === wantedType);
          setCustomerSubs(matching);
          if (matching.length > 0) {
            // Pre-select the first matching abo and auto-fill the price
            setAutoUseSubId(matching[0].id);
            if (bookingType === 'training' && !pricePerPerson) {
              setPricePerPerson(String(matching[0].price_per_lesson));
            }
          } else {
            setAutoUseSubId('');
          }
        } catch (e) { setCustomerSubs([]); setAutoUseSubId(''); }
        // also load active vouchers for this customer
        try {
          const { data: vs } = await API.get(`/vouchers/user/${selectedCustomer}`);
          setCustomerVouchers(Array.isArray(vs) ? vs : []);
        } catch (e) { setCustomerVouchers([]); }
      })();
    } else { setCustomerSubs([]); setAutoUseSubId(''); setCustomerVouchers([]); setSelectedVoucher(''); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer, bookingType]);

  const loadCourts = async () => { try { const { data } = await API.get(`/courts?type=${courtType}`); setCourts(data); } catch (e) { console.error(e); } };
  // Load the full court list once (both padel & golf). Used to attribute
  // each booking's duration to the right group in the header KPI.
  const loadAllCourts = useCallback(async () => {
    try { const { data } = await API.get('/courts'); setAllCourts(data); }
    catch (e) { console.error(e); }
  }, []);
  useEffect(() => { loadAllCourts(); }, [loadAllCourts]);
  const loadTrainers = async () => { try { const { data } = await API.get('/trainers'); setTrainers(data); } catch (e) { console.error(e); } };

  // Debounced customer search
  useEffect(() => {
    if (customerSearch.length < 2) { setCustomerResults([]); return; }
    const timer = setTimeout(async () => {
      setCustomerSearching(true);
      try {
        const { data } = await API.get(`/users/search?q=${encodeURIComponent(customerSearch)}&limit=20`);
        setCustomerResults(data);
        setShowCustomerDropdown(true);
      } catch (e) { console.error(e); }
      finally { setCustomerSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);
  const loadPricing = async (date) => { try { const { data } = await API.get(`/pricing?date=${format(date, 'yyyy-MM-dd')}&court_type=${courtType}`); setPricingInfo(data); } catch (e) { console.error(e); } };
  const loadBookings = async (date) => {
    try { setLoading(true); const { data } = await API.get(`/bookings/calendar?date=${format(date, 'yyyy-MM-dd')}`); setBookings([...data.bookings, ...data.matches]); }
    catch (e) { console.error(e); } finally { setLoading(false); setInitialLoaded(true); }
  };

  const isSlotBooked = (courtId, time) => {
    return bookings.some(b => {
      if (b.court_id !== courtId || b.status === 'cancelled') return false;
      const startMin = parseInt(b.start_time.split(':')[0]) * 60 + parseInt(b.start_time.split(':')[1]);
      const endMin = parseInt(b.end_time.split(':')[0]) * 60 + parseInt(b.end_time.split(':')[1]);
      const slotMin = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
      return slotMin >= startMin && slotMin < endMin;
    });
  };

  const getSlotBooking = (courtId, time) => {
    return bookings.find(b => {
      if (b.court_id !== courtId || b.status === 'cancelled') return false;
      const startMin = parseInt(b.start_time.split(':')[0]) * 60 + parseInt(b.start_time.split(':')[1]);
      const endMin = parseInt(b.end_time.split(':')[0]) * 60 + parseInt(b.end_time.split(':')[1]);
      const slotMin = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
      return slotMin >= startMin && slotMin < endMin;
    });
  };

  const isCourtAvailableForDuration = (courtId, startTime, durationMin) => {
    const startMinutes = parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1]);
    const endMinutes = startMinutes + durationMin;
    // Block if booking would extend past closing time
    const closeStr = pricingInfo?.closing_time || '24:00';
    const closeH = closeStr === '00:00' ? 24 : parseInt(closeStr.split(':')[0]);
    const closeM = closeStr === '00:00' ? 0 : parseInt(closeStr.split(':')[1]);
    const closingMin = closeH * 60 + closeM;
    if (endMinutes > closingMin) return false;
    for (let m = startMinutes; m < endMinutes; m += 30) {
      const h = String(Math.floor(m / 60)).padStart(2, '0');
      const min = String(m % 60).padStart(2, '0');
      if (isSlotBooked(courtId, `${h}:${min}`)) return false;
    }
    return true;
  };

  const toggleCourtSelection = (courtId) => {
    setSelectedCourts(prev => 
      prev.includes(courtId) ? prev.filter(id => id !== courtId) : [...prev, courtId]
    );
  };

  const getEndTimeOptions = () => {
    if (!selectedSlot) return [];
    const startMin = parseInt(selectedSlot.time.split(':')[0]) * 60 + parseInt(selectedSlot.time.split(':')[1]);
    // Closing time from pricingInfo ("00:00" → 24:00 = 1440min)
    const closeStr = pricingInfo?.closing_time || '24:00';
    const closeH = closeStr === '00:00' ? 24 : parseInt(closeStr.split(':')[0]);
    const closeM = closeStr === '00:00' ? 0 : parseInt(closeStr.split(':')[1]);
    const closingMin = closeH * 60 + closeM;
    const options = [];
    // From start+30min up to closing time, in 30-min steps
    for (let m = startMin + 30; m <= closingMin; m += 30) {
      const h = String(Math.floor(m / 60) % 24).padStart(2, '0');
      const min = String(m % 60).padStart(2, '0');
      const dur = m - startMin;
      options.push({ time: `${h}:${min}`, duration: dur, label: `${h}:${min} (${Math.floor(dur / 60)}h${dur % 60 > 0 ? dur % 60 : ''})` });
    }
    return options;
  };

  const handleDurationChange = (val) => {
    if (val === 'custom') {
      setShowCustomDuration(true);
      // Pre-fill with 150min (2.5h) as default custom
      if (selectedSlot) {
        const startMin = parseInt(selectedSlot.time.split(':')[0]) * 60 + parseInt(selectedSlot.time.split(':')[1]);
        const endMin = startMin + 150;
        const h = String(Math.floor(endMin / 60)).padStart(2, '0');
        const m = String(endMin % 60).padStart(2, '0');
        setCustomEndTime(`${h}:${m}`);
      }
    } else {
      setBookingDuration(val);
      setShowCustomDuration(false);
    }
  };

  const applyCustomEndTime = () => {
    if (!selectedSlot || !customEndTime) return;
    const startMin = parseInt(selectedSlot.time.split(':')[0]) * 60 + parseInt(selectedSlot.time.split(':')[1]);
    const endMin = parseInt(customEndTime.split(':')[0]) * 60 + parseInt(customEndTime.split(':')[1]);
    const dur = endMin - startMin;
    if (dur > 0 && dur % 30 === 0) {
      setBookingDuration(String(dur));
      setShowCustomDuration(false);
    }
  };

  const openCourtEdit = (court) => {
    setCourtEditForm({ name: court.name, description: court.description || '', is_indoor: court.is_indoor ?? true });
    setEditingCourt(court);
  };

  const saveCourtEdit = async () => {
    if (!editingCourt) return;
    try {
      await API.put(`/courts/${editingCourt.id}`, courtEditForm);
      toast.success(L('Court updated!', 'Platz aktualisiert!'));
      setEditingCourt(null);
      loadCourts();
      loadBookings(selectedDate);
    } catch (e) { toast.error(L('Error', 'Fehler')); }
  };

  // Click empty slot → new booking
  const handleSlotClick = (court, time) => {
    if (isSlotBooked(court.id, time)) return;
    setSelectedSlot({ court, time, date: selectedDate });
    setSelectedCustomer('');
    setCustomerSearch('');
    setSelectedCustomerName('');
    setCustomerResults([]);
    setShowCustomerDropdown(false);
    setBookingNotes('');
    setBookingType('regular');
    setSelectedTrainer('');
    setLessonPrice('');
    setSelectedVoucher('');
    setPricePerPerson('');
    setParticipants('1');
    setTrainingAutoPrice(null);
    setTrainingPriceOverride(false);
    setCustomPrice('');
    setMultiCourt(false);
    setSelectedCourts([court.id]);
    setIsRecurring(false);
    setRecurringEndDate('');
    setShowCustomDuration(false);
    setCustomEndTime('');
    setShowBookingModal(true);
  };

  // Click booked slot → detail popup
  const handleBookingClick = (e, booking) => {
    e.stopPropagation();
    if (booking.is_match) return; // Don't edit open matches here
    setDetailBooking(booking);
    setIsEditing(false);
    setShowDeleteOptions(false);
    setShowDetailModal(true);
  };

  // Start editing
  const startEditing = () => {
    const b = detailBooking;
    setEditForm({
      court_id: b.court_id,
      start_time: b.start_time,
      duration_minutes: String(b.duration_minutes),
      booking_type: b.booking_type || 'regular',
      trainer_id: b.trainer_id || '',
      lesson_price: b.booking_type === 'training' ? String(b.amount || 100) : '',
      price_per_person: b.booking_type === 'training' && b.price_per_person != null ? String(b.price_per_person) : '',
      participants: b.booking_type === 'training' ? String(b.participants || 1) : '1',
      custom_price: b.booking_type !== 'training' ? String(b.amount ?? '') : '',
      notes: b.notes || '',
    });
    setIsEditing(true);
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!detailBooking) return;
    setEditLoading(true);
    try {
      const payload = {};
      const b = detailBooking;

      if (editForm.court_id !== b.court_id) payload.court_id = editForm.court_id;
      if (editForm.start_time !== b.start_time) payload.start_time = editForm.start_time;
      if (parseInt(editForm.duration_minutes) !== b.duration_minutes) payload.duration_minutes = parseInt(editForm.duration_minutes);
      if (editForm.booking_type !== (b.booking_type || 'regular')) payload.booking_type = editForm.booking_type;
      if (editForm.notes !== (b.notes || '')) payload.notes = editForm.notes;
      if (editForm.trainer_id !== (b.trainer_id || '')) payload.trainer_id = editForm.trainer_id;
      if (editForm.booking_type === 'training') {
        const parsedPP = parseFloat(editForm.price_per_person);
        const parts = Math.max(1, parseInt(editForm.participants) || 1);
        if (Number.isFinite(parsedPP)) {
          payload.price_per_person = parsedPP;
          payload.participants = parts;
        } else if (editForm.lesson_price !== '' && editForm.lesson_price != null) {
          const parsed = parseFloat(editForm.lesson_price);
          if (Number.isFinite(parsed)) payload.lesson_price = parsed;
          if (parts !== (detailBooking.participants || 1)) payload.participants = parts;
        } else if (parts !== (detailBooking.participants || 1)) {
          payload.participants = parts;
        }
      } else {
        // Regular court booking — allow admin to override the price (e.g. for legacy vouchers).
        const cp = parseFloat(editForm.custom_price);
        if (Number.isFinite(cp) && cp >= 0 && Math.abs(cp - (b.amount || 0)) > 0.001) {
          payload.custom_price = cp;
        }
      }

      if (Object.keys(payload).length === 0) {
        toast.info(L('No changes made', 'Keine Änderungen'));
        setIsEditing(false);
        setEditLoading(false);
        return;
      }

      const { data } = await API.put(`/bookings/${detailBooking.id}`, payload);
      toast.success(L('Booking updated!', 'Buchung aktualisiert!'));
      setDetailBooking(data);
      setIsEditing(false);
      // If the booking moved to a different day, jump the calendar there so
      // the admin doesn't have to hunt for it.
      if (data?.date && data.date !== format(selectedDate, 'yyyy-MM-dd')) {
        const jump = parseISO(data.date);
        if (isValidDate(jump)) {
          setSelectedDate(jump);
          return; // the useEffect on selectedDate will trigger loadBookings
        }
      }
      await loadBookings(selectedDate);
    } catch (error) {
      const msg = error.response?.data?.detail || L('Update failed', 'Aktualisierung fehlgeschlagen');
      toast.error(msg);
      if (error.response?.status === 404) {
        setShowDetailModal(false);
        setDetailBooking(null);
        loadBookings(selectedDate);
      }
    } finally { setEditLoading(false); }
  };

  // Toggle paid / pending for a booking
  const togglePaid = async (paid, opts = {}) => {
    if (!detailBooking) return;
    if (paid && opts.amount != null && !(await chargeCardIfNeeded(opts.payment_method, opts.amount, `Buchung ${detailBooking.court_name || ''} ${detailBooking.start_time || ''}`.trim()))) return;
    try {
      const body = { payment_status: paid ? 'paid' : 'pending' };
      if (opts.payment_method) body.payment_method = opts.payment_method;
      const { data } = await API.put(`/bookings/${detailBooking.id}/payment-status`, body);
      const kasseMsg = data.kasse_entry_created ? ` · ${L('Added to cash register', 'In Kasse erfasst')}` : '';
      toast.success((paid ? L('Marked as paid', 'Als bezahlt markiert') : L('Marked as pending', 'Als unbezahlt markiert')) + kasseMsg);
      // Update local state so the dialog button flips immediately
      setDetailBooking(prev => prev ? { ...prev, payment_status: data.payment_status, paid_participants: paid ? (prev.participants || 1) : 0 } : prev);
      setShowFullPayMethod(false);
      loadBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
  };

  // Delete booking — single instance
  const handleDeleteBooking = async () => {
    if (!detailBooking) return;
    setConfirmAction({
      message: L('Permanently delete this booking?', 'Diese Buchung endgültig löschen?'),
      onConfirm: async () => {
        try {
          await API.delete(`/bookings/${detailBooking.id}`);
          toast.success(L('Booking deleted', 'Buchung gelöscht'));
        } catch (e) {
          if (e.response?.status === 404) {
            toast.info(L('Booking was already removed', 'Buchung wurde bereits entfernt'));
          } else {
            toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
          }
        }
        setConfirmAction(null);
        setShowDetailModal(false);
        setDetailBooking(null);
        loadBookings(selectedDate);
      }
    });
  };

  const getBatchCount = () => {
    if (!detailBooking?.batch_id) return 0;
    return bookings.filter(b => b.batch_id === detailBooking.batch_id && b.status !== 'cancelled').length;
  };

  const handleDeleteBatch = async () => {
    if (!detailBooking?.batch_id) return;
    const count = getBatchCount();
    setConfirmAction({
      message: L(
        `Permanently delete all ${count} court bookings from this group?`,
        `Alle ${count} Platzbuchungen dieser Gruppe endgültig löschen?`
      ),
      onConfirm: async () => {
        try {
          const { data } = await API.delete(`/bookings/batch/${detailBooking.batch_id}`);
          toast.success(`${data.deleted_count} ${L('bookings deleted', 'Buchungen gelöscht')}`);
        } catch (e) {
          toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
        }
        setConfirmAction(null);
        setShowDetailModal(false);
        setDetailBooking(null);
        loadBookings(selectedDate);
      }
    });
  };

  // Delete all future recurring bookings
  const handleDeleteAllFuture = async () => {
    if (!detailBooking?.recurring_id) return;
    setConfirmAction({
      message: L('Delete this and ALL future recurring bookings?', 'Diese und ALLE zukünftigen wiederkehrenden Buchungen löschen?'),
      onConfirm: async () => {
        try {
          await API.delete(`/recurring-bookings/${detailBooking.recurring_id}?delete_future=true`);
          toast.success(L('Recurring booking and all future instances deleted', 'Wiederkehrende Buchung und alle zukünftigen Termine gelöscht'));
        } catch (e) {
          toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
        }
        setConfirmAction(null);
        setShowDetailModal(false);
        setDetailBooking(null);
        loadBookings(selectedDate);
      }
    });
  };

  // Delete every FUTURE session of a trainer-lesson / group-training /
  // junior-training series. Uses the dedicated `/trainer/recurring-lessons/…`
  // endpoint (admin + staff + trainer) which cascades refunds (Abo lessons,
  // vouchers) and cleans Kasse rows for each removed booking.
  const handleDeleteTrainingSeries = async () => {
    if (!detailBooking?.recurring_group_id) return;
    setConfirmAction({
      message: L(
        'Delete this and ALL future lessons in this recurring training series? Abo & voucher refunds are handled automatically.',
        'Diese und ALLE zukünftigen Lektionen dieser Trainingsserie löschen? Abo- und Gutschein-Rückerstattungen werden automatisch verbucht.',
      ),
      onConfirm: async () => {
        try {
          await API.delete(`/trainer/recurring-lessons/${detailBooking.recurring_group_id}`);
          toast.success(L('Recurring training series deleted', 'Trainingsserie gelöscht'));
        } catch (e) {
          toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
        }
        setConfirmAction(null);
        setShowDetailModal(false);
        setDetailBooking(null);
        loadBookings(selectedDate);
      },
    });
  };

  // Skip just this one date of a recurring booking
  const handleSkipThisDate = async () => {
    if (!detailBooking?.recurring_id) return;
    try {
      await API.post(`/recurring-bookings/${detailBooking.recurring_id}/skip`, { date: detailBooking.date });
      toast.success(L('This date removed from recurring booking', 'Dieser Termin aus der Serienbuchung entfernt'));
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
    setShowDetailModal(false);
    setDetailBooking(null);
    loadBookings(selectedDate);
  };

  // Cancel booking (soft)
  const handleCancelBooking = async () => {
    if (!detailBooking) return;
    setConfirmAction({
      message: L('Cancel this booking?', 'Buchung stornieren?'),
      onConfirm: async () => {
        try {
          const { data } = await API.put(`/bookings/${detailBooking.id}/cancel`);
          if (data?.refund?.amount) {
            toast.success(L(
              `Booking cancelled — ${data.refund.amount.toFixed(2)} CHF refund issued (Datatrans).`,
              `Buchung storniert — ${data.refund.amount.toFixed(2)} CHF Rückerstattung ausgelöst (Datatrans).`
            ));
          } else {
            toast.success(L('Booking cancelled', 'Buchung storniert'));
          }
        } catch (e) {
          if (e.response?.status === 404) {
            toast.info(L('Booking was already removed', 'Buchung wurde bereits entfernt'));
          } else {
            toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
          }
        }
        setConfirmAction(null);
        setShowDetailModal(false);
        setDetailBooking(null);
        loadBookings(selectedDate);
      }
    });
  };

  const calculatePrice = () => {
    // Admin/staff/trainer override wins for regular court bookings (used for
    // legacy Playtomic voucher deductions at the historical price).
    if (bookingType !== 'training' && customPrice !== '') {
      const cp = parseFloat(customPrice);
      if (Number.isFinite(cp) && cp >= 0) return cp;
    }
    if (!selectedSlot || !pricingInfo) return 0;
    const startHour = parseInt(selectedSlot.time.split(':')[0]);
    const durationHours = parseInt(bookingDuration) / 60;
    let pricePerCourt;
    if (pricingInfo.is_weekend) pricePerCourt = 68 * durationHours;
    else if (startHour >= 17) pricePerCourt = 68 * durationHours;
    else {
      const endHour = startHour + durationHours;
      if (endHour <= 17) pricePerCourt = 58 * durationHours;
      else {
        const hoursBefore17 = 17 - startHour;
        const hoursAfter17 = durationHours - hoursBefore17;
        pricePerCourt = (58 * hoursBefore17) + (68 * hoursAfter17);
      }
    }
    return pricePerCourt;
  };

  const calculateTotalPrice = () => {
    const perCourt = calculatePrice();
    const courtCount = multiCourt ? selectedCourts.length : 1;
    return perCourt * courtCount;
  };

  const handleCreateBooking = async () => {
    if (!selectedSlot) return;
    const courtsToBook = multiCourt ? selectedCourts : [selectedSlot.court.id];
    if (courtsToBook.length === 0) {
      toast.error(L('Please select at least one court', 'Bitte mindestens einen Platz wählen'));
      return;
    }

    // Recurring booking flow
    if (isRecurring) {
      if (!recurringEndDate) {
        toast.error(L('Please set an end date', 'Bitte Enddatum setzen'));
        return;
      }
      setBookingLoading(true);
      try {
        let successCount = 0;
        let failCount = 0;
        let conflictInfo = [];
        let skippedSummary = []; // for series created with skip_conflicts
        // Will be filled IFF the first attempt comes back with 409 conflicts and
        // the admin opts in to "skip & create the rest".
        let skipConflictsRetry = false;
        const startDate = format(selectedSlot.date, 'yyyy-MM-dd');
        const weekday = selectedSlot.date.getDay() === 0 ? 6 : selectedSlot.date.getDay() - 1; // Convert JS Sunday=0 to Python Monday=0

        // pendingConflictsPerCourt collects 409 responses across all courts so
        // we can ask the admin ONCE (not per court).
        const pendingConflicts = {};

        const submitOne = async (courtId, skipConflicts) => {
          await API.post('/recurring-bookings', {
            court_id: courtId,
            start_date: startDate,
            end_date: recurringEndDate,
            weekday,
            start_time: selectedSlot.time,
            duration_minutes: parseInt(bookingDuration),
            customer_id: selectedCustomer || null,
            notes: bookingNotes || null,
            booking_type: bookingType,
            trainer_id: bookingType === 'training' ? (selectedTrainer || null) : null,
            price_per_person: bookingType === 'training' && pricePerPerson ? parseFloat(pricePerPerson) : null,
            participants: bookingType === 'training' ? Math.max(1, parseInt(participants) || 1) : 1,
            auto_use_subscription_id: autoUseSubId || null,
            skip_conflicts: !!skipConflicts,
          });
        };

        for (const courtId of courtsToBook) {
          try {
            await submitOne(courtId, false);
            successCount++;
          } catch (e) {
            const detail = e.response?.data?.detail;
            const courtName = courts.find(c => c.id === courtId)?.name || courtId;
            if (detail && typeof detail === 'object' && Array.isArray(detail.conflict_dates)) {
              pendingConflicts[courtId] = { courtName, dates: detail.conflict_dates };
            } else {
              failCount++;
              conflictInfo.push(`${courtName}: ${detail?.message || detail || L('error', 'Fehler')}`);
            }
          }
        }

        // If we have any conflicts, ask the admin once and optionally retry.
        const courtIdsWithConflicts = Object.keys(pendingConflicts);
        if (courtIdsWithConflicts.length > 0) {
          const totalConflictDates = courtIdsWithConflicts.reduce(
            (acc, cid) => acc + pendingConflicts[cid].dates.length, 0
          );
          const preview = courtIdsWithConflicts
            .map(cid => `${pendingConflicts[cid].courtName}: ${pendingConflicts[cid].dates.slice(0, 3).join(', ')}${pendingConflicts[cid].dates.length > 3 ? `, +${pendingConflicts[cid].dates.length - 3}…` : ''}`)
            .join('\n');
          skipConflictsRetry = window.confirm(L(
            `Found ${totalConflictDates} conflicting date(s):\n\n${preview}\n\nSkip those dates and create the rest of the series?`,
            `${totalConflictDates} Konflikt-Datum/Daten gefunden:\n\n${preview}\n\nDiese Daten überspringen und den Rest der Serie erstellen?`
          ));
          if (skipConflictsRetry) {
            for (const cid of courtIdsWithConflicts) {
              try {
                await submitOne(cid, true);
                successCount++;
                const n = pendingConflicts[cid].dates.length;
                skippedSummary.push(`${pendingConflicts[cid].courtName}: ${n} ${L('skipped', 'übersprungen')}`);
              } catch (e2) {
                failCount++;
                conflictInfo.push(`${pendingConflicts[cid].courtName}: ${e2.response?.data?.detail?.message || e2.response?.data?.detail || L('error', 'Fehler')}`);
              }
            }
          } else {
            // Admin clicked Cancel — count as failed
            for (const cid of courtIdsWithConflicts) {
              failCount++;
              conflictInfo.push(`${pendingConflicts[cid].courtName}: ${pendingConflicts[cid].dates.slice(0, 3).join(', ')}${pendingConflicts[cid].dates.length > 3 ? '…' : ''}`);
            }
          }
        }

        if (successCount > 0) {
          let msg = `${successCount} ${L('recurring series created', 'Serien erstellt')}`;
          if (skippedSummary.length > 0) msg += ` · ${skippedSummary.join(' / ')}`;
          if (failCount > 0) msg += ` (${failCount} ${L('failed', 'fehlgeschlagen')})`;
          toast.success(msg, { duration: 6000 });
        }
        if (conflictInfo.length > 0 && successCount === 0) {
          toast.error(conflictInfo.join(' | '), { duration: 8000 });
        }
        if (successCount === 0 && conflictInfo.length === 0) {
          toast.error(L('All failed', 'Alle fehlgeschlagen'));
        }
        setShowBookingModal(false);
        await loadBookings(selectedDate);
      } catch (error) {
        toast.error(L('Error', 'Fehler'));
      } finally { setBookingLoading(false); }
      return;
    }

    // Single booking flow
    setBookingLoading(true);
    const batchId = courtsToBook.length > 1 ? crypto.randomUUID() : null;
    try {
      let successCount = 0;
      let failCount = 0;
      for (const courtId of courtsToBook) {
        const payload = {
          court_id: courtId,
          date: format(selectedSlot.date, 'yyyy-MM-dd'),
          start_time: selectedSlot.time,
          duration_minutes: parseInt(bookingDuration),
          use_credits: false,
          notes: bookingNotes || null,
          booking_type: bookingType,
          batch_id: batchId,
        };
        if (selectedCustomer) payload.customer_id = selectedCustomer;
        if (autoUseSubId) payload.auto_use_subscription_id = autoUseSubId;
        if (selectedVoucher) payload.voucher_id = selectedVoucher;
        if (bookingType !== 'training' && customPrice !== '') {
          const parsedCP = parseFloat(customPrice);
          if (Number.isFinite(parsedCP) && parsedCP >= 0) payload.custom_price = parsedCP;
        }
        if (bookingType === 'training') {
          if (selectedTrainer) payload.trainer_id = selectedTrainer;
          const parts = Math.max(1, parseInt(participants) || 1);
          const parsedPP = parseFloat(pricePerPerson);
          if (Number.isFinite(parsedPP)) {
            payload.price_per_person = parsedPP;
            payload.participants = parts;
          } else {
            const parsedPrice = parseFloat(lessonPrice);
            payload.lesson_price = Number.isFinite(parsedPrice) ? parsedPrice : 100;
            payload.participants = parts;
          }
        }
        try {
          await API.post('/bookings', payload);
          successCount++;
        } catch (e) {
          failCount++;
          const courtName = courts.find(c => c.id === courtId)?.name || courtId;
          console.error(`Failed to book ${courtName}:`, e.response?.data?.detail);
        }
      }
      if (successCount > 0) {
        const msg = courtsToBook.length > 1
          ? `${successCount} ${L('bookings created', 'Buchungen erstellt')}${failCount > 0 ? `, ${failCount} ${L('failed', 'fehlgeschlagen')}` : ''}`
          : L('Booking created!', 'Buchung erstellt!');
        toast.success(msg);
      }
      if (failCount > 0 && successCount === 0) {
        toast.error(L('All bookings failed', 'Alle Buchungen fehlgeschlagen'));
      }
      setShowBookingModal(false);
      await loadBookings(selectedDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || L('Booking failed', 'Buchung fehlgeschlagen'));
    } finally { setBookingLoading(false); }
  };

  const navigateDate = (direction) => setSelectedDate(prev => addDays(prev, direction));

  const bookedSlots = bookings.filter(b => b.status !== 'cancelled').length;
  const revenue = bookings.filter(b => b.status !== 'cancelled').reduce((sum, b) => sum + (b.amount || 0), 0);

  // Total court hours for the selected day — only "real" court bookings,
  // excluding trainer lessons, group trainings, junior trainings and adult
  // academy sessions (those hours are tracked separately in the Trainer
  // Schedule / Academy views). Split into padel vs golf so the header shows
  // "Padel: X h · Golf: Y h" instead of one mixed total (Feb 2026).
  //
  // Deduplication note (Feb 2026): the /bookings/calendar endpoint returns
  // BOTH the real booking AND the open_match row once a match has a court
  // reserved. Without filtering, every open-match slot was counted twice
  // (~1.5 h shown as 3 h). We skip `is_match` entries in the total — the
  // paired booking already contributes the same duration.
  const trainingTypes = new Set(['training', 'group_training', 'junior_training', 'adult_academy']);
  const courtTypeById = React.useMemo(() => {
    const map = {};
    for (const c of allCourts) map[c.id] = c.type || 'padel';
    return map;
  }, [allCourts]);

  // Series-position badge (e.g. "3/10") for recurring bookings. Groups by
  // `recurring_group_id` (trainer recurring series) OR `recurring_id`
  // (regular court recurring bookings). Shown as a tiny bottom-left badge.
  // Needs the FULL booking collection, not just today's slice, so admin
  // sees the right number on any day — we fetch via /bookings/by-series
  // the first time a group is encountered.
  const [seriesTotals, setSeriesTotals] = React.useState({});
  const seriesPositionByBookingId = React.useMemo(() => {
    // Build groups ONLY from currently-visible bookings; fetch missing totals async.
    const groups = {};
    for (const b of bookings) {
      const gid = b.recurring_group_id || b.recurring_id;
      if (!gid || b.status === 'cancelled') continue;
      if (!groups[gid]) groups[gid] = [];
      groups[gid].push(b);
    }
    const out = {};
    for (const gid in groups) {
      const sorted = [...groups[gid]].sort((a, b) =>
        (a.date || '').localeCompare(b.date || '')
        || (a.start_time || '').localeCompare(b.start_time || '')
      );
      // Prefer the server-side total if we've fetched it; fall back to local count
      const total = seriesTotals[gid] || sorted.length;
      if (total < 2) continue;
      sorted.forEach((b) => {
        // When we don't have the server total yet, we also don't know this
        // booking's REAL position in the full series, so skip rendering
        // until the fetch resolves (prevents showing a misleading "1/1"
        // during the window the user is viewing).
        if (!seriesTotals[gid]) return;
        out[b.id] = { pos: seriesTotals[gid + ':pos:' + b.id] || null, total };
      });
    }
    return out;
  }, [bookings, seriesTotals]);

  // When new recurring bookings appear, fetch the full series totals so the
  // "X/Y" badge is accurate across the entire series not just the visible day.
  React.useEffect(() => {
    const unknownGroups = new Set();
    for (const b of bookings) {
      const gid = b.recurring_group_id || b.recurring_id;
      if (gid && !seriesTotals[gid] && b.status !== 'cancelled') unknownGroups.add(gid);
    }
    if (unknownGroups.size === 0) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      for (const gid of unknownGroups) {
        try {
          const { data } = await API.get(`/bookings/by-series/${gid}`);
          const live = (data?.bookings || []).filter(x => x.status !== 'cancelled')
            .sort((a, b) =>
              (a.date || '').localeCompare(b.date || '')
              || (a.start_time || '').localeCompare(b.start_time || '')
            );
          updates[gid] = live.length;
          live.forEach((x, idx) => { updates[gid + ':pos:' + x.id] = idx + 1; });
        } catch (_) { /* ignore */ }
      }
      if (!cancelled && Object.keys(updates).length) {
        setSeriesTotals(prev => ({ ...prev, ...updates }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);
  const { padelHours, golfHours } = React.useMemo(() => {
    let padelMin = 0;
    let golfMin = 0;
    for (const b of bookings) {
      if (b.status === 'cancelled') continue;
      if (b.is_match) continue; // paired booking is already counted below
      if (trainingTypes.has(b.booking_type)) continue;
      const dur = b.duration_minutes || 0;
      // Fallback to 'padel' if the court isn't in our map yet (e.g. very
      // first render before /courts resolves) — matches the historical
      // behaviour of counting everything as court hours.
      const ct = courtTypeById[b.court_id] || 'padel';
      if (ct === 'golf') golfMin += dur; else padelMin += dur;
    }
    return { padelHours: padelMin / 60, golfHours: golfMin / 60 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, courtTypeById]);
  const fmtHours = (h) => `${h.toFixed(h % 1 === 0 ? 0 : 1)} h`;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="admin-bookings">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1">{L('DAILY OVERVIEW', 'TAGESÜBERSICHT')}</h1>
          <p className="text-slate-500">{L('All bookings and courts at a glance', 'Alle Buchungen und Plätze auf einen Blick')}</p>
        </div>
        {/* Header actions: on mobile the buttons wrap into two rows and the
            date picker stretches full-width so admins on a phone can always
            reach every control without horizontal scroll. */}
        <div className="flex items-center gap-2 flex-wrap w-full md:w-auto">
          {!isTrainer && (
          <Button
            variant="outline"
            className="border-slate-200 text-slate-700 hover:bg-amber-50 hover:border-amber-300 font-medium flex-1 min-w-[9rem] md:flex-none justify-center"
            onClick={() => { setShowOverrides(true); loadOverrides(); }}
            data-testid="open-overrides-btn"
          >
            <CalendarBlank size={18} className="mr-2" />
            {L('Special days', 'Sondertage')}
            {overrides.length > 0 && <span className="ml-2 px-1.5 py-0.5 text-[10px] font-extrabold bg-amber-500 text-white rounded-full">{overrides.length}</span>}
          </Button>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50 font-medium flex-1 min-w-[9rem] md:flex-none justify-center" data-testid="admin-date-picker">
                <CalendarBlank size={18} className="mr-2" />
                {format(selectedDate, 'PP', { locale: dateLocale })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto max-w-[calc(100vw-1rem)] p-0 bg-white border-slate-200 overflow-hidden" align="end" side="bottom" sideOffset={6} avoidCollisions collisionPadding={8}>
              <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} locale={dateLocale} className="scale-[0.9] origin-top-right sm:scale-100" />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Date Nav + Stats */}
      <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
        <button onClick={() => navigateDate(-1)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="admin-prev-day"><CaretLeft size={20} className="text-slate-600" /></button>
        <h2 className="text-base sm:text-lg font-bold text-slate-800 truncate max-w-[60vw] sm:max-w-none">
          {format(selectedDate, 'EEEE, d. MMMM', { locale: dateLocale })}
        </h2>
        <button onClick={() => navigateDate(1)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="admin-next-day"><CaretRight size={20} className="text-slate-600" /></button>

        {/* Padel / Golf Tabs */}
        <div className="inline-flex p-1 bg-slate-100 rounded-xl border border-slate-200" data-testid="admin-court-type-tabs">
          <button
            onClick={() => setCourtType('padel')}
            className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${courtType === 'padel' ? 'bg-emerald-500 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
            data-testid="admin-tab-padel"
          >
            🎾 Padel
          </button>
          <button
            onClick={() => setCourtType('golf')}
            className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${courtType === 'golf' ? 'bg-emerald-500 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
            data-testid="admin-tab-golf"
          >
            ⛳ Golf
          </button>
        </div>

        <div className="ml-auto flex items-center gap-4 text-sm flex-wrap justify-end" data-testid="admin-bookings-court-hours">
          <span className="text-slate-500">
            🎾 {L('Padel', 'Padel')}:{' '}
            <strong className="text-slate-800 tabular-nums" data-testid="padel-court-hours">
              {fmtHours(padelHours)}
            </strong>
          </span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-500">
            ⛳ {L('Golf', 'Golf')}:{' '}
            <strong className="text-slate-800 tabular-nums" data-testid="golf-court-hours">
              {fmtHours(golfHours)}
            </strong>
          </span>
        </div>
      </div>

      {/* Color legend (customisable — click ⚙ to edit labels) */}
      <div className="flex items-center gap-4 text-xs text-slate-600 flex-wrap px-1" data-testid="admin-bookings-legend">
        <span className="font-semibold uppercase tracking-wider text-slate-400">{L('Legend', 'Legende')}:</span>
        {legendEntries.filter(e => e.show_in_legend).map(entry => {
          const style = COLOR_STYLES[entry.key];
          if (!style) return null;
          return (
            <span key={entry.key} className="inline-flex items-center gap-1.5" data-testid={`legend-item-${entry.key}`}>
              <span className={`inline-block w-3.5 h-3.5 rounded border ${style.dot}`} />
              {labelFor(entry)}
            </span>
          );
        })}
        <button
          type="button"
          onClick={() => {
            setLegendDraft(legendEntries.map(e => ({ ...e })));
            setShowLegendEditor(true);
          }}
          className="ml-auto inline-flex items-center gap-1.5 text-slate-400 hover:text-emerald-600 transition text-[11px] font-semibold uppercase tracking-wider"
          data-testid="edit-legend-btn"
          title={L('Edit legend labels', 'Legenden-Bezeichnungen bearbeiten')}
        >
          <GearSix size={14} weight="bold" />
          {L('Edit', 'Bearbeiten')}
        </button>
      </div>

      {/* Grid — outer wrapper allows horizontal scroll on narrow viewports
          (trainers on laptops/iPads) so all 8 courts stay reachable. Inner
          div keeps its 900px minimum width so columns don't collapse. */}
      <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm overflow-x-auto" data-testid="admin-bookings-hscroll">
        {loading && !initialLoaded ? (
          <div className="p-12 text-center text-slate-400">{L('Loading...', 'Laden...')}</div>
        ) : (
          <div className="min-w-[900px]">
            {/* Fixed court-names header row */}
            <div className="grid border-b border-slate-200 bg-slate-50 rounded-t-2xl" style={{ gridTemplateColumns: `70px repeat(${courts.length}, 1fr)` }}>
                <div className="px-1 py-2 text-center text-[10px] text-slate-400 font-semibold uppercase">{L('Time', 'Zeit')}</div>
                {courts.map(court => (
                  <div key={court.id} className="px-1 py-2 text-center border-l border-slate-200 group relative">
                    <p className="text-xs font-bold text-slate-800">{court.name}</p>
                    <button onClick={() => openCourtEdit(court)}
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-0.5 hover:bg-slate-200 rounded transition-opacity"
                      data-testid={`edit-court-${court.id}`}>
                      <GearSix size={12} className="text-slate-400" />
                    </button>
                  </div>
                ))}
              </div>
              {/* Scrollable time rows – only this area scrolls, keeping the court-names header + page header static */}
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 320px)', minHeight: '400px' }} data-testid="admin-bookings-scroll">
              {timeSlots.map((time) => {
                const hour = parseInt(time.split(':')[0]);
                const minute = parseInt(time.split(':')[1]);
                const isEvening = !pricingInfo?.is_weekend && hour >= 17;
                // Mark past slots (only when the selected date is today or earlier)
                const now = new Date();
                const todayKey = format(now, 'yyyy-MM-dd');
                const selectedKey = format(selectedDate, 'yyyy-MM-dd');
                const isPast = selectedKey < todayKey || (
                  selectedKey === todayKey && (hour < now.getHours() || (hour === now.getHours() && minute < now.getMinutes()))
                );
                return (
                  <div key={time} className="grid border-b border-slate-100" style={{ gridTemplateColumns: `70px repeat(${courts.length}, 1fr)` }}>
                    <div className={`px-1 py-0.5 text-center text-xs font-medium ${isPast ? 'bg-slate-200 text-slate-400' : isEvening ? 'bg-amber-50/50 text-amber-700' : 'bg-white text-slate-500'}`}>{time}</div>
                    {courts.map(court => {
                      const isBooked = isSlotBooked(court.id, time);
                      const booking = getSlotBooking(court.id, time);
                      const isBookingStart = booking?.start_time === time;

                      return (
                        <div key={`${court.id}-${time}`}
                          onClick={() => !isBooked ? handleSlotClick(court, time) : null}
                          onDragOver={(e) => {
                            if (draggedBooking) {
                              e.preventDefault();
                              setDropTarget({ courtId: court.id, time });
                            }
                          }}
                          onDragLeave={() => {
                            if (dropTarget && dropTarget.courtId === court.id && dropTarget.time === time) {
                              setDropTarget(null);
                            }
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            handleDropOnSlot(court, time);
                          }}
                          className={`relative min-h-[28px] border-l border-slate-100 transition-all ${
                            !isBooked
                              ? isPast ? 'bg-slate-100 hover:bg-slate-200 cursor-pointer' : isEvening ? 'bg-amber-50/30 hover:bg-amber-100/50 cursor-pointer' : 'bg-white hover:bg-emerald-50 cursor-pointer'
                              : ''
                          } ${dropTarget && dropTarget.courtId === court.id && dropTarget.time === time ? 'ring-2 ring-blue-400 ring-inset bg-blue-50' : ''}`}
                          data-testid={`admin-slot-${court.id}-${time}`}
                        >
                          {isBooked && isBookingStart && (() => {
                            const isTrainingType = booking.booking_type === 'training';
                            // A booking counts as "abo-paid" (light-green) when EITHER a
                            // 10x voucher OR a regular subscription/Golf-Jahres-Abo was
                            // charged for it — same visual, same semantic: no cash left
                            // to collect at the desk.
                            // A booking counts as "voucher/abo paid" when
                            // ANY of these hold: legacy voucher_id, legacy
                            // subscription_id, `paid_via_subscription_id`
                            // on the booking itself, or at least one share
                            // was settled via an abo/voucher.
                            const sharePaidViaAbo = Array.isArray(booking.shares) && booking.shares.some(
                              s => s.paid_via_subscription_id || s.voucher_id || s.subscription_id
                            );
                            const hasVoucher = !!(
                              booking.voucher_id ||
                              booking.subscription_id ||
                              booking.paid_via_subscription_id ||
                              sharePaidViaAbo
                            );
                            const isResizingThis = resizing?.booking.id === booking.id;
                            const effectiveDuration = isResizingThis ? resizing.currentDuration : booking.duration_minutes;
                            const slotCount = effectiveDuration / 30;
                            // `lightText` is overwritten once we know the colorKey (dark bg = white text).
                            let lightText = false;
                            // Show notes as the primary "customer" label when the booking was
                            // created BY admin/staff on behalf of a walk-in (there is no real
                            // customer row so `user_name` is the staff member's name). Otherwise
                            // show the actual customer's name and surface `notes` on a separate
                            // line further below.
                            const isStaffCreatedNote = booking.notes && (
                              booking.user_name === 'Admin'
                              || booking.user_name === 'Staff'
                              || booking.user_role === 'admin'
                              || booking.user_role === 'staff'
                              || !booking.user_name
                            );
                            const displayName = isStaffCreatedNote ? booking.notes : (booking.user_name || booking.notes || L('Reserved', 'Reserviert'));

                            let cardBg = '';
                            let borderColor = '';

                            // Compute payment coverage — SPLIT bookings use shares[], trainings use paid_participants
                            let paymentTier = 'paid'; // 'paid' | 'partial' | 'unpaid'
                            if (isTrainingType) {
                              const paid = booking.paid_participants || 0;
                              const total = booking.participants || 1;
                              paymentTier = paid === 0 ? 'unpaid' : paid >= total ? 'paid' : 'partial';
                            } else if (booking.payment_status === 'paid') {
                              // Whole-booking paid flag overrides the per-share
                              // check. Admin marks split-bookings as paid via
                              // the "Als bezahlt markieren" flow without
                              // touching the individual shares, so trusting
                              // `payment_status` first prevents the "shows
                              // Bezahlt but is still red" bug.
                              paymentTier = 'paid';
                            } else if ((booking.num_players || 1) > 1 && Array.isArray(booking.shares) && booking.shares.length > 0) {
                              const paid = booking.shares.filter(s => s.is_paid).length;
                              const total = booking.shares.length;
                              paymentTier = paid === 0 ? 'unpaid' : paid >= total ? 'paid' : 'partial';
                            } else {
                              paymentTier = booking.payment_status === 'paid' ? 'paid' : 'unpaid';
                            }

                            // 6-colour automatic palette (see COLOR_STYLES for
                            // legend + rationale). Priority order:
                            //   1. Training + junior academy → gray
                            //   2. Training paid vs unpaid   → green / light-green
                            //   3. Court via 10x voucher      → purple
                            //   4. Court paid vs unpaid       → blue / red
                            const isJuniorTraining = isTrainingType && (booking.academy_category === 'junior');
                            const isAdultAcademy = booking.lesson_type === 'adult_academy' || booking.academy_category === 'adult';
                            let colorKey;
                            if (isTrainingType) {
                              if (isJuniorTraining) colorKey = 'training_junior';
                              else if (isAdultAcademy) colorKey = 'training_adult';
                              else if (paymentTier === 'paid') colorKey = 'training_paid';
                              else colorKey = 'training_unpaid'; // includes 'partial' — X/Y badge shown separately
                            } else if (hasVoucher) {
                              colorKey = 'court_voucher';
                            } else if (paymentTier === 'paid') {
                              colorKey = 'court_paid';
                            } else {
                              colorKey = 'court_unpaid'; // includes 'partial' + Fix-Courts
                            }
                            const style = COLOR_STYLES[colorKey];
                            cardBg = style.bg;
                            borderColor = style.border;
                            // Contrast: dark backgrounds want white text, pastel ones want dark text.
                            const darkBgKeys = new Set(['court_unpaid', 'training_paid', 'training_unpaid', 'training_junior']);
                            const useLightText = darkBgKeys.has(colorKey);
                            lightText = useLightText;


                            return (
                              <div
                                draggable={!isResizingThis}
                                onDragStart={(e) => {
                                  if (isResizingThis) { e.preventDefault(); return; }
                                  e.stopPropagation();
                                  e.dataTransfer.effectAllowed = 'move';
                                  e.dataTransfer.setData('text/plain', booking.id);
                                  setDraggedBooking(booking);
                                }}
                                onDragEnd={() => {
                                  setDraggedBooking(null);
                                  setDropTarget(null);
                                }}
                                className={`absolute left-0 right-0 top-0 ${cardBg} ${borderColor} border rounded p-1 z-10 overflow-hidden cursor-grab active:cursor-grabbing hover:brightness-95 transition-all ${draggedBooking?.id === booking.id ? 'opacity-40' : ''} ${isResizingThis ? 'ring-2 ring-blue-400' : ''}`}
                                style={{ height: `${slotCount * 29 - 1}px` }}
                                data-testid={`booking-card-${booking.id}`}
                                onClick={(e) => { e.stopPropagation(); if (!isResizingThis) handleBookingClick(e, booking); }}
                                title={`${displayName}${booking.notes && booking.notes !== displayName ? ' • ' + booking.notes : ''}${booking.trainer_name ? ' • ' + booking.trainer_name : ''}${booking.payment_status === 'paid' ? ' • ' + L('Paid', 'Bezahlt') : ''} • ${L('Drag to move', 'Ziehen zum Verschieben')}`}
                              >
                                {/* Bottom-left: recurring series position (e.g. "3/10").
                                    Only rendered when we know the authoritative total
                                    from /bookings/by-series — avoids showing a bogus
                                    "1/1" while the fetch is in flight. */}
                                {(() => {
                                  const sp = seriesPositionByBookingId[booking.id];
                                  if (booking.abo_sessions) return <AboSessionBadges sessions={booking.abo_sessions} className="absolute bottom-0.5 left-0.5 right-0.5 z-20" testPrefix={`abo-session-${booking.id}`} />;
                                  if (!sp || !sp.pos) return null;
                                  return (
                                    <span
                                      className={`absolute bottom-0.5 left-0.5 px-1 py-[1px] rounded text-[8px] font-extrabold leading-none ${lightText ? 'bg-white/25 text-white' : 'bg-slate-800 text-white'}`}
                                      data-testid={`series-pos-${booking.id}`}
                                      title={L('Recurring booking', 'Serienbuchung')}
                                    >
                                      {sp.pos}/{sp.total}
                                    </span>
                                  );
                                })()}

                                {/* Top-right: paid indicator or X/Y badge */}
                                {isTrainingType && (booking.participants || 1) > 1 ? (
                                  <span
                                    className={`absolute top-0.5 right-0.5 text-[8px] font-extrabold px-1 rounded leading-none ${
                                      (booking.paid_participants || 0) >= (booking.participants || 1)
                                        ? 'bg-emerald-700 text-white'
                                        : (booking.paid_participants || 0) > 0
                                          ? 'bg-amber-400 text-amber-900'
                                          : 'bg-white/70 text-emerald-800'
                                    }`}
                                    data-testid={`paid-badge-${booking.id}`}
                                  >
                                    {(booking.paid_participants || 0)}/{booking.participants}
                                  </span>
                                ) : booking.payment_status === 'paid' ? (
                                  <span
                                    className={`absolute top-0.5 right-0.5 px-1 py-[1px] rounded text-[8px] font-extrabold tracking-wider leading-none flex items-center gap-0.5 ${lightText ? 'bg-emerald-200 text-emerald-900' : 'bg-emerald-700 text-white'}`}
                                    data-testid={`booking-paid-${booking.id}`}
                                  >
                                    <CheckCircle size={9} weight="fill" /> {L('PAID', 'BEZAHLT')}
                                  </span>
                                ) : (Array.isArray(booking.match_players) || ((booking.num_players || 1) > 1 && Array.isArray(booking.shares))) ? (() => {
                                  const isMt = Array.isArray(booking.match_players);
                                  const total = isMt ? (booking.match_max_players || 4) : booking.shares.length;
                                  const ownPaid = isMt ? booking.match_players.filter(p => p.paid).length : booking.shares.filter(s => s.is_paid).length;
                                  const paid = Math.min(total, Math.max(ownPaid, booking.tab_paid_shares || 0));
                                  const cls = paid === total ? 'bg-emerald-700 text-white' : paid > 0 ? 'bg-amber-400 text-amber-900' : 'bg-red-500 text-white';
                                  return (
                                    <span
                                      className={`absolute top-0.5 right-0.5 px-1 py-[1px] rounded text-[8px] font-extrabold tracking-wider leading-none ${cls}`}
                                      title={`${paid}/${total} ${L('shares paid', 'Anteile bezahlt')}`}
                                      data-testid={`shares-badge-${booking.id}`}
                                    >
                                      {paid}/{total}
                                    </span>
                                  );
                                })() : null}

                                {/* Line 1: TRAINING badge + Name */}
                                <div className="flex items-center gap-1 pr-6">
                                  {isTrainingType && (
                                    <span
                                      className={`shrink-0 px-1 py-[1px] rounded text-[9px] font-extrabold tracking-wider leading-none ${lightText ? 'bg-white/25 text-white' : 'bg-emerald-800 text-white'}`}
                                      data-testid={`training-badge-${booking.id}`}
                                    >
                                      {booking.academy_group_id ? 'ACADEMY' : 'TRAINING'}
                                    </span>
                                  )}
                                  <p className={`text-[12px] font-bold leading-tight truncate ${lightText ? 'text-white' : 'text-slate-900'}`}>
                                    {booking.recurring_id && <ArrowsClockwise size={10} weight="bold" className="inline mr-0.5 flex-shrink-0" />}
                                    {booking.academy_group_name || displayName}
                                  </p>
                                </div>

                                {/* Line 2: time (visible for ≥2 slots).
                                    For 1-hour bookings we merge time + trainer here to save one
                                    line so the notes get real breathing room. */}
                                {slotCount >= 2 && (
                                  <p className={`text-[11px] font-medium leading-tight ${lightText ? 'text-white/85' : 'text-slate-600'}`}>
                                    {booking.start_time}-{booking.end_time}
                                    {slotCount === 2 && isTrainingType && booking.trainer_name && (
                                      <span className={`ml-1.5 font-bold ${lightText ? 'text-emerald-50' : 'text-emerald-900'}`}>· {booking.trainer_name}</span>
                                    )}
                                  </p>
                                )}

                                {/* Line 3: trainer name — only for 3+ slots (1.5h+). For 1-hour
                                    bookings the trainer is already merged into the time line above. */}
                                {isTrainingType && booking.trainer_name && slotCount >= 3 && (
                                  <p className={`text-[12px] font-bold leading-tight truncate ${lightText ? 'text-emerald-50' : 'text-emerald-900'}`}
                                     data-testid={`booking-trainer-${booking.id}`}>
                                    <span className="opacity-75 mr-0.5 font-semibold">Trainer:</span>{booking.trainer_name}
                                  </p>
                                )}

                                {/* Notes — visible whenever present and different from the title.
                                    2-line clamp; 1-line only for very short (30 min) blocks. */}
                                {booking.notes && booking.notes !== displayName && (
                                  <p
                                    className={`text-[11px] font-medium italic leading-tight break-words ${lightText ? 'text-white/95' : 'text-slate-700'}`}
                                    style={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: slotCount < 2 ? 1 : 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                    }}
                                    title={booking.notes}
                                    data-testid={`booking-notes-${booking.id}`}
                                  >
                                    <NotePencil size={10} weight="bold" className="inline mr-0.5 flex-shrink-0" />
                                    {booking.notes}
                                  </p>
                                )}

                                {/* Academy: junior names list (clickable) */}
                                {((booking.academy_junior_names || []).length > 0 || (booking.academy_member_names || []).length > 0) && (() => {
                                  const adult = (booking.academy_junior_names || []).length === 0;
                                  const allNames = adult ? booking.academy_member_names : booking.academy_junior_names;
                                  const ids = adult ? [] : (booking.academy_junior_ids || []);
                                  const visible = slotCount >= 3 ? allNames : allNames.slice(0, 2);
                                  const extra = allNames.length - visible.length;
                                  return (
                                    <p className={`text-[11px] font-medium leading-tight truncate ${lightText ? 'text-white/95' : 'text-slate-700'}`} title={allNames.join(', ')}>
                                      {visible.map((n, i) => (
                                        <React.Fragment key={i}>
                                          {i > 0 && ' · '}
                                          {ids[i] ? (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); navigate(`/admin/academy?junior=${ids[i]}`); }}
                                              className="underline hover:no-underline font-semibold"
                                              data-testid={`junior-link-card-${ids[i]}`}
                                            >{n}</button>
                                          ) : n}
                                        </React.Fragment>
                                      ))}
                                      {extra > 0 && <span> +{extra}</span>}
                                    </p>
                                  );
                                })()}
                                {/* Resize handle at the bottom */}
                                <div
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setResizing({
                                      booking,
                                      startY: e.clientY,
                                      originalDuration: booking.duration_minutes,
                                      currentDuration: booking.duration_minutes,
                                    });
                                  }}
                                  className={`absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize hover:h-2 transition-all ${lightText ? 'bg-white/30 hover:bg-white/50' : 'bg-slate-700/20 hover:bg-slate-700/40'}`}
                                  title={L('Drag to resize duration', 'Ziehen für Dauer anpassen')}
                                  data-testid={`resize-handle-${booking.id}`}
                                />
                                {isResizingThis && (
                                  <span className={`absolute bottom-1.5 right-1 text-[9px] font-extrabold px-1 rounded ${lightText ? 'bg-white text-slate-900' : 'bg-slate-900 text-white'}`}>
                                    {resizing.currentDuration} min
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              {/* Closing-time marker row (e.g. "20:00" Sa/So, "24:00" Mo-Fr) */}
              {pricingInfo && (() => {
                const closeLabel = pricingInfo.closing_time === '00:00' ? '24:00' : pricingInfo.closing_time;
                return (
                  <div className="grid border-t-2 border-slate-300 bg-slate-50" style={{ gridTemplateColumns: `70px repeat(${courts.length}, 1fr)` }} data-testid="closing-time-row">
                    <div className="px-1 py-1 text-center text-xs font-bold text-slate-700">{closeLabel}</div>
                    <div className="col-span-full flex items-center px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500" style={{ gridColumn: `2 / span ${courts.length}` }}>
                      {L(`Closed from ${closeLabel}`, `Geschlossen ab ${closeLabel}`)}
                    </div>
                  </div>
                );
              })()}
              </div>
            </div>
          )}
      </div>

      {/* Price-zone legend (available/evening rates only — card status colors
          are shown in the top legend row above the grid). */}
      <div className="flex flex-wrap items-center gap-6 text-sm">
        <div className="flex items-center gap-2"><div className="w-4 h-4 bg-white border border-slate-200 rounded" /><span className="text-slate-500">{courtType === 'golf' ? L('Available (55 CHF/h)', 'Verfügbar (55 CHF/h)') : L('Available (58 CHF/h)', 'Verfügbar (58 CHF/h)')}</span></div>
        <div className="flex items-center gap-2"><div className="w-4 h-4 bg-amber-50 border border-amber-200 rounded" /><span className="text-slate-500">{courtType === 'golf' ? L('Evening (65 CHF/h)', 'Abend (65 CHF/h)') : L('Evening/Weekend (68 CHF/h)', 'Abend/Wochenende (68 CHF/h)')}</span></div>
      </div>

      {/* Legend Editor Dialog */}
      <Dialog open={showLegendEditor} onOpenChange={setShowLegendEditor}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="legend-editor-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{L('Edit calendar legend', 'Kalender-Legende bearbeiten')}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {L('Rename the labels shown in the legend and colour picker. Toggle which colours appear above the calendar. Order controls display order.',
                 'Bezeichnungen für Legende und Farbwähler umbenennen. Wähle welche Farben oberhalb des Kalenders angezeigt werden. Die Reihenfolge steuert die Anzeige.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-3">
            <div className="grid grid-cols-[auto_1fr_1fr_auto_auto] gap-2 px-2 pb-1 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <span>{L('Color', 'Farbe')}</span>
              <span>{L('Label DE', 'Bezeichnung DE')}</span>
              <span>{L('Label EN', 'Bezeichnung EN')}</span>
              <span className="text-center">{L('Show', 'Anzeigen')}</span>
              <span className="text-center">{L('Order', 'Reihenfolge')}</span>
            </div>
            {legendDraft.map((entry, idx) => {
              const style = COLOR_STYLES[entry.key];
              if (!style) return null;
              const move = (dir) => {
                setLegendDraft(prev => {
                  const arr = [...prev];
                  const swap = idx + dir;
                  if (swap < 0 || swap >= arr.length) return prev;
                  [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
                  return arr;
                });
              };
              const patch = (field, value) => {
                setLegendDraft(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
              };
              return (
                <div key={entry.key} className="grid grid-cols-[auto_1fr_1fr_auto_auto] gap-2 items-center px-2 py-1.5 rounded-lg hover:bg-slate-50" data-testid={`legend-row-${entry.key}`}>
                  <span className={`inline-block w-5 h-5 rounded-full border-2 ${style.dot}`} title={entry.key} />
                  <Input
                    value={entry.label_de}
                    onChange={(e) => patch('label_de', e.target.value)}
                    className="bg-slate-50 border-slate-200 h-8 text-sm"
                    placeholder={entry.key}
                    data-testid={`legend-label-de-${entry.key}`}
                  />
                  <Input
                    value={entry.label_en}
                    onChange={(e) => patch('label_en', e.target.value)}
                    className="bg-slate-50 border-slate-200 h-8 text-sm"
                    placeholder={entry.key}
                    data-testid={`legend-label-en-${entry.key}`}
                  />
                  <button
                    type="button"
                    onClick={() => patch('show_in_legend', !entry.show_in_legend)}
                    className={`relative w-9 h-5 rounded-full transition-colors mx-auto ${entry.show_in_legend ? 'bg-emerald-500' : 'bg-slate-300'}`}
                    data-testid={`legend-toggle-${entry.key}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${entry.show_in_legend ? 'translate-x-4' : ''}`} />
                  </button>
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      onClick={() => move(-1)}
                      disabled={idx === 0}
                      className="text-slate-400 hover:text-slate-700 disabled:opacity-30 text-xs leading-none px-1"
                      data-testid={`legend-up-${entry.key}`}
                    >▲</button>
                    <button
                      type="button"
                      onClick={() => move(1)}
                      disabled={idx === legendDraft.length - 1}
                      className="text-slate-400 hover:text-slate-700 disabled:opacity-30 text-xs leading-none px-1"
                      data-testid={`legend-down-${entry.key}`}
                    >▼</button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-2 pt-4 border-t border-slate-200 mt-4">
            <Button
              variant="outline"
              onClick={async () => {
                if (!confirm(L('Reset all labels to defaults?', 'Alle Bezeichnungen auf Werkseinstellung zurücksetzen?'))) return;
                setLegendSaving(true);
                try {
                  await API.post('/booking-legend/reset');
                  const { data } = await API.get('/booking-legend');
                  setLegendEntries(data);
                  setLegendDraft(data.map(e => ({ ...e })));
                  toast.success(L('Legend reset', 'Legende zurückgesetzt'));
                } catch (e) {
                  toast.error(L('Reset failed', 'Zurücksetzen fehlgeschlagen'));
                } finally { setLegendSaving(false); }
              }}
              disabled={legendSaving}
              className="border-slate-200 text-slate-600 text-xs"
              data-testid="legend-reset-btn"
            >
              {L('Reset to defaults', 'Werkseinstellung')}
            </Button>
            <div className="flex-1" />
            <Button
              variant="outline"
              onClick={() => setShowLegendEditor(false)}
              className="border-slate-200 text-slate-600"
              data-testid="legend-cancel-btn"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={async () => {
                setLegendSaving(true);
                try {
                  const { data } = await API.put('/booking-legend', {
                    entries: legendDraft.map(e => ({
                      key: e.key,
                      label_de: e.label_de,
                      label_en: e.label_en,
                      show_in_legend: e.show_in_legend,
                    })),
                  });
                  setLegendEntries(data);
                  toast.success(L('Legend saved', 'Legende gespeichert'));
                  setShowLegendEditor(false);
                } catch (e) {
                  toast.error(L('Save failed', 'Speichern fehlgeschlagen'));
                } finally { setLegendSaving(false); }
              }}
              disabled={legendSaving}
              className="bg-emerald-500 text-white hover:bg-emerald-600 font-bold"
              data-testid="legend-save-btn"
            >
              {legendSaving ? '...' : L('Save', 'Speichern')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Special-days (Overrides) Dialog */}
      <Dialog open={showOverrides} onOpenChange={setShowOverrides}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-xl max-h-[90vh] overflow-y-auto" data-testid="overrides-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{L('Special days', 'Sondertage')}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {L('Override opening hours for individual dates (holidays, events, maintenance).', 'Öffnungszeiten für einzelne Tage anpassen (Feiertage, Veranstaltungen, Wartung).')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-3">
            {/* Add / Update form */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">{L('Date', 'Datum')}</Label>
                  <Input type="date" value={ovForm.date} onChange={(e) => setOvForm(f => ({ ...f, date: e.target.value }))}
                    className="bg-white border-slate-300 h-9 text-sm" data-testid="ov-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">{L('Note (optional)', 'Notiz (optional)')}</Label>
                  <Input value={ovForm.note} onChange={(e) => setOvForm(f => ({ ...f, note: e.target.value }))}
                    placeholder={L('Christmas, event…', 'Feiertag, Event…')}
                    className="bg-white border-slate-300 h-9 text-sm" data-testid="ov-note" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 select-none cursor-pointer">
                <input type="checkbox" checked={ovForm.is_closed}
                  onChange={(e) => setOvForm(f => ({ ...f, is_closed: e.target.checked }))}
                  className="w-4 h-4 accent-red-500" data-testid="ov-closed" />
                <span className="font-semibold">{L('Closed all day', 'Ganzer Tag geschlossen')}</span>
              </label>
              {!ovForm.is_closed && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">{L('Opening', 'Öffnung')}</Label>
                    <Input type="time" value={ovForm.opening_time}
                      onChange={(e) => setOvForm(f => ({ ...f, opening_time: e.target.value }))}
                      className="bg-white border-slate-300 h-9 text-sm" data-testid="ov-open" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">{L('Closing', 'Schliessung')}</Label>
                    <Input type="time" value={ovForm.closing_time}
                      onChange={(e) => setOvForm(f => ({ ...f, closing_time: e.target.value }))}
                      className="bg-white border-slate-300 h-9 text-sm" data-testid="ov-close" />
                  </div>
                </div>
              )}
              <Button onClick={saveOverride}
                className="w-full bg-emerald-600 text-white hover:bg-emerald-700 font-bold"
                data-testid="ov-save">
                {L('Save special day', 'Sondertag speichern')}
              </Button>
            </div>

            {/* Bulk close range — for seasonal closures, renovations, holidays */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-2" data-testid="close-range-card">
              <div className="flex items-start gap-2">
                <span className="text-lg">🚫</span>
                <div>
                  <p className="text-sm font-bold text-red-900">{L('Close date range', 'Zeitraum schliessen')}</p>
                  <p className="text-[11px] text-red-700 leading-snug">
                    {L('Blocks ALL customer bookings for every day in the range (courts, matches, trainings). Admin/staff can still create bookings via the admin panel.',
                       'Blockiert ALLE Kundenbuchungen für jeden Tag im Zeitraum (Plätze, Matches, Trainings). Admin/Staff können weiterhin über das Panel buchen.')}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">{L('From', 'Von')}</Label>
                  <Input type="date" value={rangeForm.start_date}
                    onChange={(e) => setRangeForm(f => ({ ...f, start_date: e.target.value }))}
                    className="bg-white border-red-300 h-9 text-sm" data-testid="range-start" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">{L('Until (inclusive)', 'Bis (inkl.)')}</Label>
                  <Input type="date" value={rangeForm.end_date}
                    onChange={(e) => setRangeForm(f => ({ ...f, end_date: e.target.value }))}
                    className="bg-white border-red-300 h-9 text-sm" data-testid="range-end" />
                </div>
              </div>
              <Input value={rangeForm.note}
                onChange={(e) => setRangeForm(f => ({ ...f, note: e.target.value }))}
                placeholder={L('Reason (e.g. seasonal closure)', 'Grund (z.B. Saisonpause)')}
                className="bg-white border-red-300 h-9 text-sm" data-testid="range-note" />
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={closeDateRange} disabled={rangeSaving || !rangeForm.start_date || !rangeForm.end_date}
                  className="w-full bg-red-600 text-white hover:bg-red-700 font-bold" data-testid="close-range-btn">
                  {rangeSaving ? L('Closing…', 'Schliesse…') : L('Close range', 'Zeitraum schliessen')}
                </Button>
                <Button onClick={openDateRange} disabled={rangeSaving || !rangeForm.start_date || !rangeForm.end_date}
                  variant="outline"
                  className="w-full border-red-300 text-red-700 hover:bg-red-50 font-bold" data-testid="reopen-range-btn">
                  {L('Re-open range', 'Wieder öffnen')}
                </Button>
              </div>
            </div>

            {/* List of existing overrides */}
            <div className="space-y-1.5">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{L('Active overrides', 'Aktive Sondertage')} ({overrides.length})</p>
              {overrides.length === 0 ? (
                <p className="text-sm text-slate-400 italic text-center py-4">{L('No special days yet', 'Noch keine Sondertage')}</p>
              ) : (
                overrides.map(o => (
                  <div key={o.id} className={`flex items-center justify-between p-2 rounded-lg border ${o.is_closed ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-testid={`override-row-${o.date}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-bold text-slate-800 text-sm shrink-0">{o.date}</span>
                      {o.is_closed ? (
                        <span className="px-2 py-0.5 text-[10px] font-extrabold bg-red-500 text-white rounded-full">{L('CLOSED', 'GESCHLOSSEN')}</span>
                      ) : (
                        <span className="text-xs text-amber-800 font-mono">{o.opening_time}–{o.closing_time}</span>
                      )}
                      {o.note && <span className="text-xs text-slate-500 truncate ml-1">· {o.note}</span>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button
                        onClick={() => setOvForm({ date: o.date, is_closed: o.is_closed, opening_time: o.opening_time || '08:00', closing_time: o.closing_time || '18:00', note: o.note || '' })}
                        className="p-1 text-slate-400 hover:text-blue-600"
                        title={L('Edit', 'Bearbeiten')}
                        data-testid={`override-edit-${o.date}`}
                      ><PencilSimple size={14} /></button>
                      <button
                        onClick={() => deleteOverride(o.date)}
                        className="p-1 text-slate-400 hover:text-red-500"
                        title={L('Remove', 'Entfernen')}
                        data-testid={`override-delete-${o.date}`}
                      ><Trash size={14} /></button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ BOOKING DETAIL / EDIT MODAL ============ */}
      <Dialog open={showDetailModal} onOpenChange={(open) => { if (!open) { setShowDetailModal(false); setIsEditing(false); setPayMethodOpen(false); setPayParticipantName(''); setShowFullPayMethod(false); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="booking-detail-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {isEditing ? L('Edit Booking', 'Buchung bearbeiten') : L('Booking Details', 'Buchungsdetails')}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {isEditing ? L('Change time, court, or details', 'Zeit, Platz oder Details ändern') : L('View and manage this booking', 'Buchung anzeigen und verwalten')}
            </DialogDescription>
          </DialogHeader>

          {detailBooking && !isEditing && (
            <div className="space-y-4 mt-2">
              {/* Booking Info Card */}
              <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl space-y-3">
                <div className={`flex justify-between items-center ${detailBooking.academy_group_id ? 'hidden' : ''}`}>
                  <span className="text-slate-500">{L('Customer', 'Kunde')}</span>
                  {detailBooking.user_id ? (
                    <button
                      type="button"
                      onClick={() => { setShowDetailModal(false); navigate(`/admin/users/${detailBooking.user_id}`); }}
                      className="text-blue-600 font-bold hover:text-blue-800 hover:underline transition-colors flex items-center gap-1"
                      data-testid="booking-customer-link"
                      title={L('Open profile', 'Profil öffnen')}
                    >
                      {detailBooking.user_name}
                      <span className="text-xs opacity-60">→</span>
                    </button>
                  ) : (
                    <span className="text-slate-900 font-bold">{detailBooking.user_name}</span>
                  )}
                </div>
                {detailBooking.abo_sessions && (
                  <div className="flex justify-between items-center" data-testid="detail-abo-sessions-row">
                    <span className="text-slate-500">Abo</span>
                    <AboSessionBadges sessions={detailBooking.abo_sessions} size="sm" className="justify-end" testPrefix="detail-abo-session" />
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">{t('courts')}</span>
                  <span className="text-slate-900 font-semibold">{detailBooking.court_name}</span>
                </div>
                {detailBooking.batch_id && getBatchCount() > 1 && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">{L('Group', 'Gruppe')}</span>
                    <span className="text-blue-600 font-semibold text-sm bg-blue-50 px-2 py-0.5 rounded-lg">
                      {getBatchCount()} {L('courts booked together', 'Plätze zusammen gebucht')}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">{L('Time', 'Zeit')}</span>
                  <span className="text-slate-900 font-semibold">{detailBooking.start_time} - {detailBooking.end_time}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">{t('duration')}</span>
                  <span className="text-slate-900">{detailBooking.duration_minutes} min</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">{L('Type', 'Typ')}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    detailBooking.booking_type === 'training' 
                      ? detailBooking.voucher_id ? 'bg-emerald-700 text-white' : 'bg-emerald-100 text-emerald-700'
                      : detailBooking.voucher_id ? 'bg-blue-700 text-white' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {detailBooking.booking_type === 'training' ? L('Training', 'Training') : L('Regular', 'Normal')}
                    {detailBooking.voucher_id && ` + ${L('Voucher', 'Gutschein')}`}
                    {detailBooking.subscription_id && detailBooking.payment_method === 'golf_yearly' && ` + ${L('Golf yearly abo', 'Golf Jahres-Abo')}`}
                  </span>
                </div>
                {detailBooking.trainer_name && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">{L('Trainer', 'Trainer')}</span>
                    <span className="text-purple-700 font-semibold">{detailBooking.trainer_name}</span>
                  </div>
                )}
                {(detailBooking.booked_by_name || detailBooking.created_at) && (
                  <div className="flex justify-between items-start gap-3 border-t border-slate-200 pt-3">
                    <span className="text-slate-500">{L('Booked', 'Gebucht')}</span>
                    <div className="text-right">
                      {detailBooking.created_at && (
                        <div className="text-slate-900 font-semibold text-sm" data-testid="booking-created-at">
                          {new Date(detailBooking.created_at).toLocaleString(language === 'de' ? 'de-CH' : 'en-GB', {
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </div>
                      )}
                      {detailBooking.booked_by_name && (
                        <div className="text-xs text-slate-500 mt-0.5" data-testid="booking-booked-by">
                          {L('by', 'von')} <span className="font-medium text-slate-700">{detailBooking.booked_by_name}</span>
                          {detailBooking.booked_by_role && (
                            <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                              detailBooking.booked_by_role === 'admin' ? 'bg-red-100 text-red-700' :
                              detailBooking.booked_by_role === 'staff' ? 'bg-amber-100 text-amber-700' :
                              detailBooking.booked_by_role === 'trainer' ? 'bg-purple-100 text-purple-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {detailBooking.booked_by_role === 'admin' ? L('Admin', 'Admin') :
                               detailBooking.booked_by_role === 'staff' ? L('Reception', 'Empfang') :
                               detailBooking.booked_by_role === 'trainer' ? L('Trainer', 'Trainer') :
                               L('Customer', 'Kunde')}
                            </span>
                          )}
                          {detailBooking.booked_by_id && detailBooking.user_id &&
                           detailBooking.booked_by_id !== detailBooking.user_id &&
                           detailBooking.booked_by_role !== 'customer' && (
                            <span className="ml-1 text-[10px] text-amber-700 font-semibold">
                              {L('(for customer)', '(für Kunden)')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {detailBooking.academy_group_id && (
                  <AcademyJuniorChips booking={detailBooking} API={API} L={L}
                    onChange={(patch) => { setDetailBooking(prev => prev ? { ...prev, ...patch } : prev); loadBookings(selectedDate); }}
                    onOpenJunior={(jid) => { setShowDetailModal(false); navigate(`/admin/academy?junior=${jid}`); }} />
                )}
                <div className="flex justify-between items-center border-t border-slate-200 pt-3">
                  <span className="text-slate-900 font-semibold">{t('price')}</span>
                  <div className="text-right">
                    <span className="text-xl font-extrabold text-emerald-600">{(detailBooking.amount || 0).toFixed(2)} CHF</span>
                    {detailBooking.payment_method && (
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium ${
                        detailBooking.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                        detailBooking.payment_method === 'cash' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {detailBooking.payment_method === 'cash' ? L('Pay at Club', 'Vor Ort') :
                         detailBooking.payment_method === 'online' || detailBooking.payment_method === 'datatrans' ? L('Online', 'Online') :
                         detailBooking.payment_method === 'credits' ? L('Credits', 'Guthaben') :
                         detailBooking.payment_method === 'voucher' ? L('Voucher', 'Gutschein') :
                         detailBooking.payment_method}
                        {detailBooking.payment_status === 'paid' ? ' ✓' : 
                         detailBooking.payment_status === 'pending_cash' ? '' : 
                         detailBooking.payment_status === 'pending' ? ' ⏳' : ''}
                      </span>
                    )}
                  </div>
                </div>
                {detailBooking.notes && (
                  <div className="border-t border-slate-200 pt-3">
                    <span className="text-slate-500 text-sm">{L('Notes', 'Notizen')}: </span>
                    <span className="text-slate-700 text-sm">{detailBooking.notes}</span>
                  </div>
                )}
                {detailBooking.booking_type === 'training' && (() => {
                  const total = detailBooking.participants || 1;
                  const paid = detailBooking.paid_participants || 0;
                  const pp = detailBooking.price_per_person || (detailBooking.amount && total ? detailBooking.amount / total : 0);
                  const updatePaid = async (next, opts = {}) => {
                    const clamped = Math.max(0, Math.min(total, next));
                    const tip = opts.payment_method === 'card' ? Math.max(0, parseFloat(trainingTip) || 0) : 0;
                    if (!(await chargeCardIfNeeded(opts.payment_method, pp * (clamped - paid) + tip, `Training ${detailBooking.start_time || ''}`.trim()))) return;
                    try {
                      const body = { paid_participants: clamped };
                      if (opts.payment_method) body.payment_method = opts.payment_method;
                      if (opts.participant_name) body.participant_name = opts.participant_name;
                      const { data } = await API.put(`/bookings/${detailBooking.id}/paid-participants`, body);
                      setDetailBooking(prev => prev ? { ...prev, paid_participants: data.paid_participants, payment_status: data.payment_status } : prev);
                      loadBookings(selectedDate);
                      // Reset name for the next participant; keep panel OPEN if there are still unpaid participants
                      setPayParticipantName('');
                      if (tip > 0) {
                        try { await bookTip(API, tip, `Training ${detailBooking.user_name || ''}`.trim()); }
                        catch { toast.error(L('Tip could not be booked — please add it in the Kasse', 'Trinkgeld konnte nicht verbucht werden – bitte in der Kasse nachtragen')); }
                      }
                      setTrainingTip('');
                      setPayCount(0);
                      if (data.paid_participants >= total) {
                        setPayMethodOpen(false);
                      }
                      const kasseMsg = data.kasse_entries_created > 0 ? ` · ${L('Added to cash register', 'In Kasse erfasst')}` : '';
                      toast.success(`${data.paid_participants}/${total} ${L('paid', 'bezahlt')}${kasseMsg}`);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
                    }
                  };
                  const remaining = total - paid;
                  const cnt = payCount >= 1 && payCount <= remaining ? payCount : remaining;
                  const tipNum = Math.max(0, parseFloat(trainingTip) || 0);
                  const prepaid = [...(detailBooking.paid_via_subscriptions || []), ...(detailBooking.paid_via_vouchers || [])];
                  return (
                    <div className="border-t border-slate-200 pt-3 space-y-2" data-testid="participants-payment-block">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-700 font-semibold text-sm">{L('Payment', 'Zahlung')}</span>
                        <span className="text-xs text-slate-500">{pp > 0 ? `${pp.toFixed(2)} CHF p.P.` : ''}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 text-center bg-slate-50 rounded-lg py-1.5 border border-slate-200">
                          <span className="text-lg font-extrabold text-emerald-700" data-testid="paid-participants-count">{paid}</span>
                          <span className="text-slate-400"> / </span>
                          <span className="text-lg font-semibold text-slate-700">{total}</span>
                          <span className="text-xs text-slate-500 ml-1">{L('paid', 'bezahlt')}</span>
                        </div>
                        {paid >= total && (
                          <span className="px-3 py-2 rounded-lg bg-emerald-100 text-emerald-700 text-sm font-bold" data-testid="training-fully-paid">
                            {L('All paid', 'Alle bezahlt')} ✓
                          </span>
                        )}
                      </div>
                      {prepaid.length > 0 && (
                        <p className="text-xs text-slate-500" data-testid="training-prepaid-info">
                          {L('Already paid', 'Bereits bezahlt')}: {prepaid.map(x => `${x.client_name || ''} (${x.voucher_code ? L('Voucher', 'Gutschein') : 'Abo'})`).join(', ')}
                        </p>
                      )}
                      {paid < total && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-3" data-testid="pay-method-panel">
                          <Label className="text-xs text-emerald-800 font-semibold block">
                            {L('How many people are paying?', 'Wie viele zahlen?')}
                          </Label>
                          <div className="grid grid-cols-4 gap-2">
                            {Array.from({ length: remaining }).map((_, i) => {
                              const n = i + 1;
                              const isAll = n === remaining;
                              return (
                                <button
                                  key={n}
                                  onClick={() => setPayCount(n)}
                                  className={`h-12 rounded-lg font-extrabold text-lg transition-all active:scale-95 ${
                                    cnt === n
                                      ? 'bg-emerald-600 text-white shadow-md ring-2 ring-emerald-700'
                                      : 'bg-white border-2 border-emerald-200 text-emerald-800 hover:bg-emerald-100'
                                  }`}
                                  data-testid={`pay-count-${n}`}
                                >
                                  {n}{isAll && remaining > 1 ? ` (${L('all', 'alle')})` : ''}
                                </button>
                              );
                            })}
                          </div>
                          <div className="text-center text-sm font-bold text-emerald-900" data-testid="training-collect-total">
                            {L('Total', 'Total')}: {(pp * cnt).toFixed(2)} CHF
                          </div>
                          <TipInput value={trainingTip} onChange={setTrainingTip} base={pp * cnt} />
                          <div className="grid grid-cols-2 gap-2">
                            <Button size="sm"
                              onClick={() => updatePaid(paid + cnt, { payment_method: 'cash' })}
                              className="h-12 bg-amber-500 text-white hover:bg-amber-600 font-bold text-base"
                              data-testid="pay-method-cash">
                              {L('Cash', 'Bar')} {(pp * cnt).toFixed(2)}
                            </Button>
                            <Button size="sm"
                              onClick={() => updatePaid(paid + cnt, { payment_method: 'card' })}
                              className="h-12 bg-blue-600 text-white hover:bg-blue-700 font-bold text-base"
                              data-testid="pay-method-card">
                              {L('Card', 'Karte')} {(pp * cnt + tipNum).toFixed(2)}
                            </Button>
                          </div>
                          {tipNum > 0 && <p className="text-[11px] text-center text-slate-500">{L('Tip only applies to card payments', 'Trinkgeld gilt nur bei Kartenzahlung')}</p>}
                          <TrainingPrepaidPay key={`${detailBooking.id}-${paid}`} booking={detailBooking} API={API} L={L} pp={pp}
                            onPaid={(data) => {
                              setDetailBooking(prev => prev ? { ...prev, ...data } : prev);
                              loadBookings(selectedDate);
                            }} />
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                {/* Open Tab button */}
                {detailBooking.status !== 'cancelled' && detailBooking.booking_type !== 'training' && (
                  <Button onClick={async () => {
                    try {
                      const { data } = await API.post('/tabs', { booking_id: detailBooking.id });
                      navigate(`/admin/tabs/${data.id}`);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || L('Error opening tab', 'Fehler beim Öffnen'));
                    }
                  }} className="w-full bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="open-tab-btn">
                    <Receipt size={18} className="mr-2" /> {L('Open Tab', 'Tab öffnen')}
                  </Button>
                )}
                {/* Recurring badge */}
                {detailBooking.recurring_id && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-xl text-sm text-indigo-700 font-medium">
                    <CalendarBlank size={16} weight="bold" />
                    {L('Recurring / Fixed Booking', 'Wiederkehrende / Fixe Buchung')}
                  </div>
                )}

                <div className="flex gap-2 flex-wrap">
                  {detailBooking.payment_status === 'paid' ? (
                    <Button
                      onClick={() => togglePaid(false)}
                      variant="outline"
                      className="flex-1 border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold"
                      data-testid="mark-unpaid-btn">
                      <CheckCircle size={16} className="mr-2" weight="fill" /> {L('Paid ✓ (click to undo)', 'Bezahlt ✓ (zum Rückgängig machen)')}
                    </Button>
                  ) : detailBooking.booking_type === 'training' ? null : (
                    <Button
                      onClick={() => setShowFullPayMethod(o => !o)}
                      className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-semibold"
                      data-testid="mark-paid-btn">
                      <CheckCircle size={16} className="mr-2" /> {L('Mark as paid', 'Als bezahlt markieren')}
                    </Button>
                  )}
                </div>

                {detailBooking.lesson_type === 'adult_academy' && (
                  <div className="mt-4" data-testid="detail-adult-academy">
                    <p className="font-bold text-violet-900 text-sm mb-2">Adult Academy · {L('participants & payments', 'Teilnehmer & Zahlungen')}</p>
                    <AdultAcademyPanel API={API} L={L} bookingId={detailBooking.id} />
                  </div>
                )}

                {Array.isArray(detailBooking.match_players) && (
                  <MatchPlayersPaid booking={detailBooking} L={L} />
                )}

                {/* Split-payment shares panel — for padel bookings with multiple players */}
                {(detailBooking.num_players || 1) > 1 && !detailBooking.match_id && (
                  <BookingSharesPanel
                    bookingId={detailBooking.id}
                    onChange={() => loadBookings()}
                  />
                )}

                {showFullPayMethod && detailBooking.payment_status !== 'paid' && (() => {
                  // Compute remaining amount to post to Kasse (skip already paid participants for trainings)
                  const isTr = detailBooking.booking_type === 'training';
                  const total = detailBooking.participants || 1;
                  const paidP = detailBooking.paid_participants || 0;
                  const pp = detailBooking.price_per_person || (detailBooking.amount && total ? detailBooking.amount / total : 0);
                  const remaining = isTr ? Math.max(0, pp * (total - paidP)) : (detailBooking.amount || 0);
                  return (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2" data-testid="full-pay-method-panel">
                      <Label className="text-xs text-emerald-800 font-semibold">
                        {L(`Collect ${remaining.toFixed(2)} CHF`, `${remaining.toFixed(2)} CHF kassieren`)}
                      </Label>
                      <div className="grid grid-cols-3 gap-2">
                        <Button size="sm"
                          onClick={() => togglePaid(true, { payment_method: 'cash', amount: remaining })}
                          className="h-9 bg-amber-500 text-white hover:bg-amber-600 font-bold"
                          data-testid="full-pay-cash">
                          {L('Cash', 'Bar')} {remaining.toFixed(0)}
                        </Button>
                        <Button size="sm"
                          onClick={() => togglePaid(true, { payment_method: 'card', amount: remaining })}
                          className="h-9 bg-blue-600 text-white hover:bg-blue-700 font-bold"
                          data-testid="full-pay-card">
                          {L('Card', 'Karte')} {remaining.toFixed(0)}
                        </Button>
                        <Button size="sm"
                          onClick={() => togglePaid(true, { payment_method: 'css_coins', amount: remaining })}
                          className="h-9 bg-fuchsia-600 text-white hover:bg-fuchsia-700 font-bold"
                          title={L('CSS Coins — not counted in daily till (~2 weeks payout)', 'CSS Coins — nicht im Tages-Total (Auszahlung ~2 Wochen)')}
                          data-testid="full-pay-css">
                          CSS
                        </Button>
                      </div>
                      <button
                        onClick={() => togglePaid(true)}
                        className="w-full text-[11px] text-slate-500 hover:text-slate-700 underline"
                        data-testid="full-pay-skip">
                        {L('Skip – only mark as paid (no cash register entry)', 'Überspringen – nur markieren (kein Kasseneintrag)')}
                      </button>
                    </div>
                  );
                })()}

                {!detailBooking.match_id && detailBooking.booking_type !== 'training' && detailBooking.status !== 'cancelled'
                  && detailBooking.payment_status !== 'paid' && courts.find(c => c.id === detailBooking.court_id)?.type !== 'golf'
                  && new Date(`${detailBooking.date}T${detailBooking.start_time}:00`) > new Date()
                  && (!isTrainer || user.can_block_trainer_time) && (
                  <Button onClick={() => setOpenMatchFor(detailBooking)} variant="outline"
                    className="w-full border-emerald-400 text-emerald-700 hover:bg-emerald-50 font-semibold" data-testid="booking-to-open-match-btn">
                    {L('Open as open match (find players)', 'Als Open Match öffnen (Spieler suchen)')}
                  </Button>
                )}

                <div className="flex gap-2">
                  <Button onClick={startEditing} className="flex-1 bg-slate-800 text-white hover:bg-slate-700 font-semibold" data-testid="edit-booking-btn">
                    <PencilSimple size={16} className="mr-2" /> {L('Edit', 'Bearbeiten')}
                  </Button>
                  {trainerLocked(detailBooking) ? (
                    <div className="flex-1 flex items-center justify-center text-xs font-semibold text-slate-500 border border-slate-200 rounded-md px-2 text-center" data-testid="trainer-cancel-locked">
                      {user.can_block_trainer_time
                        ? L('Paid — only admin/reception can cancel', 'Bezahlt — nur Admin/Empfang kann stornieren')
                        : L('Only admin/reception/head coach can cancel', 'Nur Admin/Empfang/Head Coach kann stornieren')}
                    </div>
                  ) : (
                  <Button onClick={() => setShowDeleteOptions(!showDeleteOptions)} variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50 font-semibold" data-testid="delete-options-btn">
                    <Trash size={16} className="mr-2" /> {L('Delete', 'Löschen')}
                  </Button>
                  )}
                </div>

                {/* Delete options panel */}
                {showDeleteOptions && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    {detailBooking.recurring_id ? (
                      <>
                        <p className="text-sm text-red-700 font-medium mb-2">{L('This is a recurring booking. What do you want to delete?', 'Dies ist eine Serienbuchung. Was möchten Sie löschen?')}</p>
                        <Button onClick={handleSkipThisDate} variant="outline" className="w-full border-amber-400 text-amber-800 hover:bg-amber-100 font-semibold justify-start bg-white" data-testid="delete-this-only-btn">
                          <X size={16} className="mr-2" /> {L('Only this date', 'Nur diesen Termin')}
                        </Button>
                        <Button onClick={handleDeleteAllFuture} variant="outline" className="w-full border-red-400 text-red-700 hover:bg-red-100 font-semibold justify-start bg-white" data-testid="delete-all-future-btn">
                          <Trash size={16} className="mr-2" /> {L('This & all future bookings', 'Diesen & alle zukünftigen Buchungen')}
                        </Button>
                      </>
                    ) : detailBooking.recurring_group_id ? (
                      <>
                        <p className="text-sm text-red-700 font-medium mb-2">
                          {L('This lesson is part of a recurring training series. What do you want to delete?',
                             'Diese Lektion gehört zu einer Trainings-Serie. Was möchten Sie löschen?')}
                        </p>
                        <Button onClick={handleDeleteBooking} variant="outline" className="w-full border-amber-400 text-amber-800 hover:bg-amber-100 font-semibold justify-start bg-white" data-testid="delete-lesson-only-btn">
                          <X size={16} className="mr-2" /> {L('Only this lesson', 'Nur diese Lektion')}
                        </Button>
                        <Button onClick={handleDeleteTrainingSeries} variant="outline" className="w-full border-red-500 text-red-700 hover:bg-red-100 font-semibold justify-start bg-white" data-testid="delete-training-series-btn">
                          <Trash size={16} className="mr-2" /> {L('This & all future lessons in this series', 'Diese & alle zukünftigen Lektionen der Serie')}
                        </Button>
                      </>
                    ) : detailBooking.batch_id && getBatchCount() > 1 ? (
                      <>
                        <p className="text-sm text-red-700 font-medium mb-2">
                          {L(`This booking is part of a group of ${getBatchCount()} courts. What do you want to delete?`,
                             `Diese Buchung gehört zu einer Gruppe von ${getBatchCount()} Plätzen. Was möchten Sie löschen?`)}
                        </p>
                        <Button onClick={handleCancelBooking} variant="outline" className="w-full border-amber-400 text-amber-800 hover:bg-amber-100 font-semibold justify-start bg-white" data-testid="cancel-booking-btn">
                          <X size={16} className="mr-2" /> {L('Cancel this court only', 'Nur diesen Platz stornieren')}
                        </Button>
                        <Button onClick={handleDeleteBooking} variant="outline" className="w-full border-red-400 text-red-700 hover:bg-red-100 font-semibold justify-start bg-white" data-testid="delete-booking-btn">
                          <Trash size={16} className="mr-2" /> {L('Delete this court only', 'Nur diesen Platz löschen')}
                        </Button>
                        <Button onClick={handleDeleteBatch} variant="outline" className="w-full border-red-600 text-red-800 hover:bg-red-200 font-semibold justify-start bg-white" data-testid="delete-batch-btn">
                          <Trash size={16} className="mr-2" /> {L(`Delete all ${getBatchCount()} courts`, `Alle ${getBatchCount()} Plätze löschen`)}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button onClick={handleCancelBooking} variant="outline" className="w-full border-amber-400 text-amber-800 hover:bg-amber-100 font-semibold justify-start bg-white" data-testid="cancel-booking-btn">
                          <X size={16} className="mr-2" /> {L('Cancel booking (keep record)', 'Buchung stornieren (Eintrag behalten)')}
                        </Button>
                        <Button onClick={handleDeleteBooking} variant="outline" className="w-full border-red-400 text-red-700 hover:bg-red-100 font-semibold justify-start bg-white" data-testid="delete-booking-btn">
                          <Trash size={16} className="mr-2" /> {L('Permanently delete', 'Endgültig löschen')}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* EDIT FORM */}
          {detailBooking && isEditing && (
            <div className="space-y-4 mt-2">
              {/* Court */}
              <div className="space-y-2">
                <Label className="label-text flex items-center gap-1.5"><MapPin size={14} /> {t('courts')}</Label>
                <Select value={editForm.court_id} onValueChange={(v) => setEditForm(f => ({ ...f, court_id: v }))}>
                  <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="edit-court"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {courts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Start Time */}
              <div className="space-y-2">
                <Label className="label-text flex items-center gap-1.5"><Clock size={14} /> {L('Start Time', 'Startzeit')}</Label>
                <Select value={editForm.start_time} onValueChange={(v) => setEditForm(f => ({ ...f, start_time: v }))}>
                  <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="edit-time"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 max-h-48">
                    {timeSlots.map(ts => <SelectItem key={ts} value={ts}>{ts}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <Label className="label-text">{t('duration')}</Label>
                <Select value={['30','60','90','120'].includes(editForm.duration_minutes) ? editForm.duration_minutes : 'custom'} onValueChange={(v) => {
                  if (v === 'custom') {
                    // Set a default custom value (150min)
                    setEditForm(f => ({ ...f, duration_minutes: '150' }));
                  } else {
                    setEditForm(f => ({ ...f, duration_minutes: v }));
                  }
                }}>
                  <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="edit-duration"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="60">60 min (1h)</SelectItem>
                    <SelectItem value="90">90 min (1.5h)</SelectItem>
                    <SelectItem value="120">120 min (2h)</SelectItem>
                    <SelectItem value="custom">{L('Custom...', 'Individuell...')}</SelectItem>
                  </SelectContent>
                </Select>
                {!['30','60','90','120'].includes(editForm.duration_minutes) && (
                  <Input type="number" value={editForm.duration_minutes} onChange={(e) => setEditForm(f => ({ ...f, duration_minutes: e.target.value }))}
                    className="bg-slate-50 border-slate-200 mt-2" placeholder="min" min="30" step="30" data-testid="edit-custom-duration" />
                )}
              </div>

              {/* Booking Type */}
              <div className="space-y-2">
                <Label className="label-text">{L('Booking Type', 'Buchungsart')}</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setEditForm(f => ({ ...f, booking_type: 'regular', lesson_price: '' }))}
                    className={`p-3 border rounded-xl text-sm font-medium transition-all ${editForm.booking_type === 'regular' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}>
                    {L('Regular', 'Normal')}
                  </button>
                  <button onClick={() => setEditForm(f => ({ ...f, booking_type: 'training', lesson_price: f.lesson_price || '100' }))}
                    className={`p-3 border rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${editForm.booking_type === 'training' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}>
                    <Barbell size={16} /> {L('Training', 'Training')}
                  </button>
                </div>
              </div>

              {/* Training fields */}
              {editForm.booking_type === 'training' && (
                <>
                  <div className="space-y-2">
                    <Label className="label-text">{L('Trainer / Coach', 'Trainer / Coach')}</Label>
                    <Select value={editForm.trainer_id || 'none'} onValueChange={(v) => setEditForm(f => ({ ...f, trainer_id: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="edit-trainer"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="none">{L('No trainer', 'Kein Trainer')}</SelectItem>
                        {trainers.filter(t => t.is_active).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label className="label-text">{L('Price / Person', 'Preis / Person')} (CHF)</Label>
                      <Input type="number" step="5"
                        value={editForm.price_per_person}
                        onChange={(e) => setEditForm(f => ({ ...f, price_per_person: e.target.value }))}
                        className="bg-slate-50 border-slate-200" placeholder="35" data-testid="edit-price-per-person" />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-text">{L('Participants', 'Teilnehmer')}</Label>
                      <Input type="number" min="1" max="8" step="1"
                        value={editForm.participants}
                        onChange={(e) => setEditForm(f => ({ ...f, participants: e.target.value }))}
                        className="bg-slate-50 border-slate-200" data-testid="edit-participants" />
                    </div>
                  </div>
                  {(() => {
                    const pp = parseFloat(editForm.price_per_person);
                    const parts = Math.max(1, parseInt(editForm.participants) || 1);
                    if (Number.isFinite(pp) && pp > 0) {
                      return (
                        <p className="text-xs text-emerald-700 font-semibold">
                          {L('Total', 'Gesamt')}: {(pp * parts).toFixed(2)} CHF ({parts} × {pp.toFixed(2)})
                        </p>
                      );
                    }
                    return null;
                  })()}
                  <div className="space-y-2">
                    <Label className="label-text text-slate-500">{L('…or fixed lesson total (CHF)', '…oder Lektion Gesamtpreis (CHF)')}</Label>
                    <Input type="number" step="10" value={editForm.lesson_price}
                      onChange={(e) => setEditForm(f => ({ ...f, lesson_price: e.target.value }))}
                      className="bg-slate-50 border-slate-200" placeholder="100" data-testid="edit-lesson-price"
                      disabled={editForm.price_per_person !== '' && !Number.isNaN(parseFloat(editForm.price_per_person))} />
                  </div>
                </>
              )}

              {/* Custom price override for regular court bookings (edit) */}
              {editForm.booking_type !== 'training' && (
                <div className="space-y-2 p-3 bg-indigo-50 border border-indigo-200 rounded-xl">
                  <Label className="label-text text-indigo-900 font-semibold">
                    {L('Price (CHF)', 'Preis (CHF)')}
                  </Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={editForm.custom_price}
                    onChange={(e) => setEditForm(f => ({ ...f, custom_price: e.target.value }))}
                    className="bg-white border-indigo-200"
                    placeholder={L('e.g. 40 for legacy voucher rate', 'z.B. 40 für alten Gutschein-Tarif')}
                    data-testid="edit-custom-price"
                  />
                  <p className="text-[11px] text-indigo-700 leading-relaxed">
                    {L(
                      'Overrides the dynamic price. Use this to re-price a booking that will be paid by a legacy voucher at the old rate.',
                      'Überschreibt den Standardpreis. Nützlich, um eine Buchung auf den alten Gutschein-Tarif anzupassen.'
                    )}
                  </p>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label className="label-text">{L('Notes', 'Notizen')}</Label>
                <Input value={editForm.notes} onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  className="bg-slate-50 border-slate-200" placeholder={L('e.g. Phone reservation', 'z.B. Telefon-Reservierung')} data-testid="edit-notes" />
              </div>

              {/* Color override removed — automatic colouring based on
                  booking type + payment. See COLOR_STYLES in the calendar. */}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setIsEditing(false)} className="flex-1 border-slate-200 text-slate-600">
                  {t('cancel')}
                </Button>
                <Button onClick={handleSaveEdit} disabled={editLoading}
                  className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold" data-testid="save-edit-btn">
                  {editLoading ? '...' : L('Save Changes', 'Änderungen speichern')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {openMatchFor && (
        <OpenMatchFromBooking booking={openMatchFor} open={!!openMatchFor} onClose={() => setOpenMatchFor(null)}
          onDone={() => { setOpenMatchFor(null); setShowDetailModal(false); setDetailBooking(null); loadBookings(selectedDate); }} />
      )}


      {/* ============ CREATE BOOKING MODAL ============ */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-booking-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{L('New Booking', 'Neue Buchung')}</DialogTitle>
            <DialogDescription className="text-slate-500">{L('Create booking for customer', 'Buchung für Kunden erstellen')}</DialogDescription>
          </DialogHeader>

          {selectedSlot && (
            <div className="space-y-4 mt-4">
              {/* Slot Info */}
              <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl space-y-2">
                <div className="flex justify-between"><span className="text-slate-500">{t('courts')}</span><span className="text-slate-900 font-bold">{multiCourt ? `${selectedCourts.length} ${L('courts', 'Plätze')}` : selectedSlot.court.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">{L('Date', 'Datum')}</span><span className="text-slate-900">{format(selectedSlot.date, 'PP', { locale: dateLocale })}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">{L('Time', 'Zeit')}</span><span className="text-slate-900">{selectedSlot.time}</span></div>
              </div>

              {/* Multi-Court Toggle */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    const next = !multiCourt;
                    setMultiCourt(next);
                    if (next) setSelectedCourts([selectedSlot.court.id]);
                    else setSelectedCourts([selectedSlot.court.id]);
                  }}
                  className={`relative w-11 h-6 rounded-full transition-colors ${multiCourt ? 'bg-emerald-500' : 'bg-slate-300'}`}
                  data-testid="multi-court-toggle"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${multiCourt ? 'translate-x-5' : ''}`} />
                </button>
                <span className="text-sm font-medium text-slate-700">{L('Multiple Courts', 'Mehrere Plätze')}</span>
              </div>

              {/* Court Selection Grid */}
              {multiCourt && (
                <div className="grid grid-cols-4 gap-2" data-testid="court-selection-grid">
                  {courts.map(court => {
                    const available = isCourtAvailableForDuration(court.id, selectedSlot.time, parseInt(bookingDuration));
                    const selected = selectedCourts.includes(court.id);
                    return (
                      <button
                        key={court.id}
                        disabled={!available}
                        onClick={() => toggleCourtSelection(court.id)}
                        className={`p-2.5 rounded-xl text-xs font-bold border-2 transition-all ${
                          !available ? 'bg-red-50 border-red-200 text-red-300 cursor-not-allowed line-through' :
                          selected ? 'bg-emerald-50 border-emerald-500 text-emerald-700' :
                          'bg-white border-slate-200 text-slate-600 hover:border-slate-400'
                        }`}
                        data-testid={`court-select-${court.id}`}
                      >
                        {court.name}
                        {!available && <span className="block text-[10px] font-normal">{L('booked', 'belegt')}</span>}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Recurring Toggle */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsRecurring(!isRecurring)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${isRecurring ? 'bg-blue-500' : 'bg-slate-300'}`}
                  data-testid="recurring-toggle"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isRecurring ? 'translate-x-5' : ''}`} />
                </button>
                <span className="text-sm font-medium text-slate-700">{L('Recurring', 'Wiederkehrend')}</span>
              </div>

              {isRecurring && selectedSlot && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <ArrowsClockwise size={16} className="text-blue-600" />
                    <span className="text-blue-700 font-medium">
                      {L('Every', 'Jeden')} {format(selectedSlot.date, 'EEEE', { locale: dateLocale })} {L('at', 'um')} {selectedSlot.time}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-blue-600 font-medium">{L('Until (end date)', 'Bis (Enddatum)')}</Label>
                    <Input type="date" value={recurringEndDate} onChange={(e) => setRecurringEndDate(e.target.value)}
                      min={format(selectedSlot.date, 'yyyy-MM-dd')}
                      className="bg-white border-blue-200 text-slate-900" data-testid="recurring-end-date" />
                  </div>
                  {recurringEndDate && (() => {
                    const start = selectedSlot.date;
                    const end = new Date(recurringEndDate);
                    const weeks = Math.max(1, Math.round((end - start) / (7 * 24 * 60 * 60 * 1000)));
                    const courtCount = multiCourt ? selectedCourts.length : 1;
                    return (
                      <p className="text-xs text-blue-600">
                        ≈ {weeks} {L('weeks', 'Wochen')} × {courtCount} {L(courtCount === 1 ? 'court' : 'courts', courtCount === 1 ? 'Platz' : 'Plätze')} = {weeks * courtCount} {L('bookings', 'Buchungen')}
                      </p>
                    );
                  })()}
                </div>
              )}

              {/* Booking Type — hidden for golf (no trainers on simulators) */}
              {courtType !== 'golf' && (
              <div className="space-y-2">
                <Label className="label-text">{L('Booking Type', 'Buchungsart')}</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => { setBookingType('regular'); setLessonPrice(''); }}
                    className={`p-3 border rounded-xl text-sm font-medium transition-all ${bookingType === 'regular' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
                    data-testid="booking-type-regular">
                    {L('Regular', 'Normal')}
                  </button>
                  <button onClick={() => { setBookingType('training'); if (!lessonPrice) setLessonPrice('100'); }}
                    className={`p-3 border rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${bookingType === 'training' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
                    data-testid="booking-type-training">
                    <Barbell size={16} /> {L('Training', 'Training')}
                  </button>
                </div>
              </div>
              )}

              {/* Duration */}
              <div className="space-y-2">
                <Label className="label-text">{t('duration')}</Label>
                <Select value={['30','60','90','120'].includes(bookingDuration) ? bookingDuration : 'custom'} onValueChange={handleDurationChange}>
                  <SelectTrigger className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="60">60 min (1h)</SelectItem>
                    <SelectItem value="90">90 min (1.5h)</SelectItem>
                    <SelectItem value="120">120 min (2h)</SelectItem>
                    <SelectItem value="custom">{L('Custom...', 'Individuell...')}</SelectItem>
                  </SelectContent>
                </Select>
                {showCustomDuration && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                    <Label className="text-xs text-slate-500">{L('End time', 'Endzeit')}</Label>
                    <div className="flex gap-2">
                      <Select value={customEndTime} onValueChange={setCustomEndTime}>
                        <SelectTrigger className="bg-white border-slate-200 flex-1"><SelectValue placeholder="--:--" /></SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 max-h-48">
                          {getEndTimeOptions().map(opt => (
                            <SelectItem key={opt.time} value={opt.time}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button onClick={applyCustomEndTime} className="bg-emerald-500 text-white hover:bg-emerald-600 font-bold px-4" data-testid="apply-custom-duration">
                        OK
                      </Button>
                    </div>
                    {customEndTime && selectedSlot && (() => {
                      const startMin = parseInt(selectedSlot.time.split(':')[0]) * 60 + parseInt(selectedSlot.time.split(':')[1]);
                      const endMin = parseInt(customEndTime.split(':')[0]) * 60 + parseInt(customEndTime.split(':')[1]);
                      const dur = endMin - startMin;
                      return <p className="text-xs text-emerald-600 font-medium">{selectedSlot.time} – {customEndTime} = {Math.floor(dur / 60)}h{dur % 60 > 0 ? ` ${dur % 60}min` : ''}</p>;
                    })()}
                  </div>
                )}
                {!showCustomDuration && !['30','60','90','120'].includes(bookingDuration) && (
                  <p className="text-xs text-emerald-600 font-medium">{L('Custom duration:', 'Individuelle Dauer:')} {bookingDuration} min ({Math.floor(parseInt(bookingDuration) / 60)}h{parseInt(bookingDuration) % 60 > 0 ? ` ${parseInt(bookingDuration) % 60}min` : ''})</p>
                )}
              </div>

              {/* Customer */}
              <div className="space-y-2">
                <Label className="label-text">{L('Customer', 'Kunde')} *</Label>
                <div className="relative">
                  <Input
                    value={selectedCustomer ? selectedCustomerName : customerSearch}
                    onChange={(e) => {
                      if (selectedCustomer) { setSelectedCustomer(''); setSelectedCustomerName(''); }
                      setCustomerSearch(e.target.value);
                      if (e.target.value.length < 2) setShowCustomerDropdown(false);
                    }}
                    onFocus={() => { if (customerResults.length > 0) setShowCustomerDropdown(true); }}
                    className={`bg-slate-50 border-slate-200 ${selectedCustomer ? 'text-emerald-700 font-semibold bg-emerald-50 border-emerald-300' : ''}`}
                    placeholder={L('Search by name, email or phone...', 'Name, E-Mail oder Telefon suchen...')}
                    data-testid="select-customer"
                  />
                  {selectedCustomer && (
                    <button onClick={() => { setSelectedCustomer(''); setSelectedCustomerName(''); setCustomerSearch(''); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      <X size={16} />
                    </button>
                  )}
                  {customerSearching && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">...</span>}
                  {showCustomerDropdown && customerResults.length > 0 && !selectedCustomer && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto" data-testid="customer-dropdown">
                      {customerResults.map(u => (
                        <button key={u.id} onClick={() => {
                          setSelectedCustomer(u.id);
                          setSelectedCustomerName(`${u.name} (${u.email})`);
                          setCustomerSearch('');
                          setShowCustomerDropdown(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm transition-colors border-b border-slate-50 last:border-0"
                        data-testid={`customer-option-${u.id}`}>
                          <span className="font-medium text-slate-800">{u.name}</span>
                          <span className="text-slate-400 ml-2 text-xs">{u.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {showCustomerDropdown && customerSearch.length >= 2 && customerResults.length === 0 && !customerSearching && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-center text-sm text-slate-400">
                      {L('No customers found', 'Keine Kunden gefunden')}
                    </div>
                  )}
                </div>
              </div>

              {/* Training-specific fields */}
              {bookingType === 'training' && (
                <>
                  <div className="space-y-2">
                    <Label className="label-text">{L('Trainer / Coach', 'Trainer / Coach')}</Label>
                    <Select value={selectedTrainer} onValueChange={setSelectedTrainer}>
                      <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="select-trainer">
                        <SelectValue placeholder={L('Select trainer...', 'Trainer wählen...')} />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        {trainers.filter(t => t.is_active).map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label className="label-text">{L('Price / Person', 'Preis / Person')} (CHF)</Label>
                      <Input type="number" step="5" value={pricePerPerson}
                        onChange={(e) => { setPricePerPerson(e.target.value); setTrainingPriceOverride(true); }}
                        className="bg-slate-50 border-slate-200" placeholder="35" data-testid="price-per-person" />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-text">{L('Participants', 'Teilnehmer')}</Label>
                      <Input type="number" min="1" max="8" step="1" value={participants} onChange={(e) => setParticipants(e.target.value)}
                        className="bg-slate-50 border-slate-200" placeholder="1" data-testid="participants-count" />
                    </div>
                  </div>
                  {trainingAutoPrice && !trainingPriceOverride && (
                    <p className="text-[11px] text-emerald-700 flex items-center gap-1.5 -mt-1" data-testid="training-auto-price-hint">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold uppercase tracking-wide text-[10px]">
                        {trainingAutoPrice.tier === 'peak' ? L('Peak', 'Peak') : L('Off-Peak', 'Off-Peak')}
                      </span>
                      <span>
                        {L(
                          `Auto-priced from padelarena.ch: ${trainingAutoPrice.price_per_person} CHF × ${trainingAutoPrice.participants}`,
                          `Automatisch von padelarena.ch: ${trainingAutoPrice.price_per_person} CHF × ${trainingAutoPrice.participants}`
                        )}
                      </span>
                      <button type="button" onClick={() => setTrainingPriceOverride(true)} className="text-emerald-600 underline hover:text-emerald-700">
                        {L('Override', 'Manuell')}
                      </button>
                    </p>
                  )}
                  {trainingPriceOverride && (
                    <button type="button"
                      onClick={() => { setTrainingPriceOverride(false); setPricePerPerson(''); }}
                      className="text-[11px] text-emerald-600 hover:underline -mt-1"
                      data-testid="training-restore-auto-price"
                    >
                      ← {L('Use official price', 'Offiziellen Preis verwenden')}
                    </button>
                  )}
                  {(() => {
                    const pp = parseFloat(pricePerPerson);
                    const parts = Math.max(1, parseInt(participants) || 1);
                    if (!Number.isFinite(pp) || pp <= 0) return null;
                    const weeks = (isRecurring && recurringEndDate && selectedSlot)
                      ? Math.max(1, Math.round((new Date(recurringEndDate) - selectedSlot.date) / (7 * 24 * 60 * 60 * 1000)))
                      : 1;
                    const courtCount = multiCourt ? Math.max(1, selectedCourts.length) : 1;
                    const perSession = pp * parts;
                    const grand = perSession * weeks * courtCount;
                    return (
                      <p className="text-xs text-emerald-700 font-semibold" data-testid="price-total-preview">
                        {L('Total', 'Gesamt')}: {grand.toFixed(2)} CHF
                        <span className="text-emerald-600/80 font-normal">
                          {' '}({parts} × {pp.toFixed(2)}
                          {weeks > 1 ? <> × {weeks} {L('weeks', 'Wochen')}</> : null}
                          {courtCount > 1 ? <> × {courtCount} {L('courts', 'Plätze')}</> : null}
                          )
                        </span>
                      </p>
                    );
                  })()}

                  <div className="space-y-2">
                    <Label className="label-text text-slate-500">{L('…or fixed lesson total (CHF)', '…oder Lektion Gesamtpreis (CHF)')}</Label>
                    <Input type="number" step="10" value={lessonPrice} onChange={(e) => setLessonPrice(e.target.value)}
                      className="bg-slate-50 border-slate-200" placeholder="100" data-testid="lesson-price"
                      disabled={pricePerPerson !== '' && !Number.isNaN(parseFloat(pricePerPerson))} />
                  </div>
                </>
              )}

              {/* Abo (Subscription) auto-deduct */}
              {selectedCustomer && customerSubs.length > 0 && (
                <div className="space-y-2 p-4 bg-purple-50 border border-purple-200 rounded-xl" data-testid="abo-deduct-block">
                  <div className="flex items-center justify-between">
                    <Label className="label-text text-purple-900 font-semibold">
                      {L('Deduct from subscription', 'Vom Abo abziehen')}
                    </Label>
                    <button
                      type="button"
                      onClick={() => setAutoUseSubId(autoUseSubId ? '' : (customerSubs[0]?.id || ''))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoUseSubId ? 'bg-purple-600' : 'bg-slate-300'}`}
                      data-testid="abo-deduct-toggle"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoUseSubId ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  {autoUseSubId && customerSubs.length > 1 && (
                    <Select value={autoUseSubId} onValueChange={setAutoUseSubId}>
                      <SelectTrigger className="bg-white border-purple-200" data-testid="abo-deduct-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-purple-200">
                        {customerSubs.map(s => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.type === 'training' ? L('Training', 'Training') : L('Court', 'Court')}-Abo · {s.lessons_remaining}/{s.total_lessons} · {Number(s.price_per_lesson).toFixed(0)} CHF/{s.type === 'court' ? L('court hour', 'Court-Stunde') : L('lesson', 'Lektion')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {autoUseSubId && (() => {
                    const s = customerSubs.find(x => x.id === autoUseSubId);
                    if (!s) return null;
                    const willBeDeducted = isRecurring ? L('per booking', 'pro Buchung') : '1';
                    return (
                      <p className="text-xs text-purple-700">
                        {L('Will mark booking as paid and deduct', 'Markiert Buchung als bezahlt und zieht ab')}: <strong>{willBeDeducted}</strong> {L('lesson(s)', 'Lektion(en)')} · {L('Remaining', 'Verbleibend')}: <strong>{s.lessons_remaining}/{s.total_lessons}</strong>
                      </p>
                    );
                  })()}
                  {!autoUseSubId && (
                    <p className="text-xs text-slate-500">
                      {L('Customer has active subscriptions — toggle on to auto-pay this booking', 'Kunde hat aktive Abos — einschalten, um Buchung automatisch zu bezahlen')}
                    </p>
                  )}
                </div>
              )}

              {/* Voucher (Gutschein) deduction */}
              {selectedCustomer && customerVouchers.length > 0 && (
                <div className="space-y-2 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="voucher-deduct-block">
                  <div className="flex items-center justify-between">
                    <Label className="label-text text-amber-900 font-semibold">
                      {L('Pay with voucher', 'Mit Gutschein bezahlen')}
                    </Label>
                    {selectedVoucher ? (
                      <button
                        type="button"
                        onClick={() => setSelectedVoucher('')}
                        className="text-xs text-amber-700 hover:text-amber-900 underline"
                        data-testid="voucher-clear"
                      >
                        {L('clear', 'entfernen')}
                      </button>
                    ) : null}
                  </div>
                  <Select value={selectedVoucher} onValueChange={setSelectedVoucher}>
                    <SelectTrigger className="bg-white border-amber-200" data-testid="voucher-select">
                      <SelectValue placeholder={L('Select voucher…', 'Gutschein wählen…')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-amber-200">
                      {customerVouchers.map(v => (
                        <SelectItem key={v.id} value={v.id} disabled={(v.remaining_balance ?? v.amount) <= 0}>
                          {v.code} · {L('Balance', 'Guthaben')}: <strong>{Number(v.remaining_balance ?? v.amount).toFixed(2)} CHF</strong>
                          {v.description ? ` · ${v.description}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedVoucher && (() => {
                    const v = customerVouchers.find(x => x.id === selectedVoucher);
                    if (!v) return null;
                    const remaining = v.remaining_balance ?? v.amount;
                    let cost;
                    if (bookingType === 'training') {
                      const pp = parseFloat(pricePerPerson);
                      const parts = Math.max(1, parseInt(participants) || 1);
                      if (Number.isFinite(pp) && pp > 0) {
                        // Per-person × participants
                        cost = pp * parts;
                      } else {
                        // Fixed lesson total (already the full price — do NOT multiply)
                        const lp = parseFloat(lessonPrice);
                        cost = Number.isFinite(lp) && lp > 0 ? lp : 100;
                      }
                    } else {
                      cost = calculatePrice();
                    }
                    const after = remaining - cost;
                    const enough = after >= -0.01;
                    return (
                      <div className={`text-xs space-y-0.5 p-2 rounded-lg ${enough ? 'bg-amber-100/60' : 'bg-red-100 border border-red-300'}`}>
                        <div className="flex justify-between"><span className="text-slate-600">{L('Current balance', 'Aktuelles Guthaben')}</span><strong>{remaining.toFixed(2)} CHF</strong></div>
                        <div className="flex justify-between"><span className="text-slate-600">{L('Booking cost', 'Buchungskosten')}</span><strong className="text-red-600">-{cost.toFixed(2)} CHF</strong></div>
                        <div className="flex justify-between border-t border-amber-300 mt-1 pt-1">
                          <span className="text-slate-700 font-semibold">{L('After', 'Nach Abzug')}</span>
                          <strong className={enough ? 'text-emerald-700' : 'text-red-600'}>{after.toFixed(2)} CHF</strong>
                        </div>
                        {!enough && (
                          <p className="text-red-700 font-semibold pt-1">
                            {L('Insufficient balance — cannot use this voucher', 'Guthaben reicht nicht — Gutschein nicht verwendbar')}
                          </p>
                        )}
                        {enough && (
                          <p className="text-amber-700 pt-1">
                            {L('Booking will be marked as PAID via voucher', 'Buchung wird als BEZAHLT (per Gutschein) markiert')}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Color override — matches the calendar legend */}
              {/* Color picker removed — colours are now fully automatic
                  based on booking type + payment status. See COLOR_STYLES. */}

              {/* Custom price override (regular court bookings only) — used
                  e.g. for legacy Playtomic vouchers that were priced at the
                  old rate. Leave blank to fall back to the dynamic price. */}
              {bookingType !== 'training' && (
                <div className="space-y-2 p-3 bg-indigo-50 border border-indigo-200 rounded-xl" data-testid="custom-price-block">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="label-text text-indigo-900 font-semibold">
                      {L('Custom price (CHF)', 'Eigener Preis (CHF)')}
                    </Label>
                    {customPrice !== '' && (
                      <button
                        type="button"
                        onClick={() => setCustomPrice('')}
                        className="text-xs text-indigo-700 hover:text-indigo-900 underline"
                        data-testid="custom-price-clear"
                      >
                        {L('use dynamic', 'zurück zu Standard')}
                      </button>
                    )}
                  </div>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={customPrice}
                    onChange={(e) => setCustomPrice(e.target.value)}
                    className="bg-white border-indigo-200"
                    placeholder={L('e.g. 40 for legacy voucher rate', 'z.B. 40 für alten Gutschein-Tarif')}
                    data-testid="custom-price-input"
                  />
                  <p className="text-[11px] text-indigo-700 leading-relaxed">
                    {L(
                      'Overrides the dynamic price. Handy for legacy vouchers priced at old rates — the voucher will deduct exactly this amount.',
                      'Überschreibt den Standardpreis. Nützlich für Legacy-Gutscheine (Playtomic) mit altem Tarif — der Gutschein wird genau um diesen Betrag reduziert.'
                    )}
                  </p>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label className="label-text">{L('Notes', 'Notizen')}</Label>
                <Input value={bookingNotes} onChange={(e) => setBookingNotes(e.target.value)}
                  className="bg-slate-50 border-slate-200" placeholder={L('e.g. Phone reservation', 'z.B. Telefon-Reservierung')} />
              </div>

              {/* Price */}
              <div className="py-4 border-t border-b border-slate-200">
                {(() => {
                  let perBooking;
                  if (bookingType === 'training') {
                    const pp = parseFloat(pricePerPerson);
                    const parts = Math.max(1, parseInt(participants) || 1);
                    if (Number.isFinite(pp) && pp > 0) {
                      perBooking = pp * parts;
                    } else {
                      const lp = parseFloat(lessonPrice);
                      perBooking = Number.isFinite(lp) && lp > 0 ? lp : 0;
                    }
                  } else {
                    perBooking = calculatePrice();
                  }
                  const courtCount = multiCourt ? Math.max(1, selectedCourts.length) : 1;
                  const weeks = (isRecurring && recurringEndDate && selectedSlot)
                    ? Math.max(1, Math.round((new Date(recurringEndDate) - selectedSlot.date) / (7 * 24 * 60 * 60 * 1000)))
                    : 1;
                  const total = perBooking * courtCount * weeks;
                  const showBreakdown = courtCount > 1 || weeks > 1;
                  return showBreakdown ? (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <span>
                          {weeks > 1 && <>{weeks} {L('weeks', 'Wochen')} × </>}
                          {courtCount > 1 && <>{courtCount} {L('courts', 'Plätze')} × </>}
                          {perBooking.toFixed(0)} CHF
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-lg text-slate-900">{L('Total', 'Gesamt')}</span>
                        <span className="text-2xl font-extrabold text-emerald-600" data-testid="booking-total-price">
                          {total.toFixed(0)} CHF
                        </span>
                      </div>
                      {isRecurring && autoUseSubId && (() => {
                        const sub = customerSubs.find(x => x.id === autoUseSubId);
                        if (!sub) return null;
                        const willDeduct = Math.min(weeks * courtCount, sub.lessons_remaining || 0);
                        return (
                          <p className="text-xs text-purple-600 pt-1">
                            <strong>{willDeduct}</strong> {L('lessons will be deducted from subscription', 'Lektionen werden vom Abo abgezogen')} ({(willDeduct * (perBooking || sub.price_per_lesson)).toFixed(2)} CHF {L('value', 'Wert')})
                          </p>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-lg text-slate-900">{t('price')}</span>
                      <span className="text-2xl font-extrabold text-emerald-600" data-testid="booking-total-price">
                        {perBooking.toFixed(0)} CHF
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowBookingModal(false)} className="flex-1 border-slate-200 text-slate-600">{t('cancel')}</Button>
                <Button onClick={handleCreateBooking} disabled={bookingLoading || (multiCourt && selectedCourts.length === 0) || (isRecurring && !recurringEndDate)}
                  className={`flex-1 font-bold text-white ${isRecurring ? 'bg-blue-500 hover:bg-blue-600' : 'bg-emerald-500 hover:bg-emerald-600'}`} data-testid="confirm-admin-booking">
                  {bookingLoading ? '...' : isRecurring
                    ? L('Create Recurring', 'Serie erstellen')
                    : multiCourt && selectedCourts.length > 1 
                      ? `${selectedCourts.length} ${L('Bookings Create', 'Buchungen erstellen')}`
                      : L('Create Booking', 'Buchung erstellen')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ CONFIRMATION DIALOG ============ */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-sm" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{L('Confirm', 'Bestätigen')}</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 mt-2">{confirmAction?.message}</p>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setConfirmAction(null)} className="flex-1 border-slate-200 text-slate-600" data-testid="confirm-cancel-btn">
              {t('cancel')}
            </Button>
            <Button onClick={() => confirmAction?.onConfirm()} className="flex-1 bg-red-500 text-white hover:bg-red-600 font-bold" data-testid="confirm-yes-btn">
              {L('Yes, proceed', 'Ja, fortfahren')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Court Edit Dialog */}
      <Dialog open={!!editingCourt} onOpenChange={(open) => { if (!open) setEditingCourt(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-sm" data-testid="court-edit-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{L('Edit Court', 'Platz bearbeiten')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="space-y-2">
              <Label className="label-text">{L('Court Name', 'Platzname')} *</Label>
              <Input value={courtEditForm.name} onChange={(e) => setCourtEditForm({...courtEditForm, name: e.target.value})}
                className="bg-slate-50 border-slate-200" data-testid="court-edit-name" />
            </div>
            <div className="space-y-2">
              <Label className="label-text">{L('Description', 'Beschreibung')}</Label>
              <Input value={courtEditForm.description} onChange={(e) => setCourtEditForm({...courtEditForm, description: e.target.value})}
                className="bg-slate-50 border-slate-200" placeholder={L('e.g. Sponsor name', 'z.B. Sponsorname')} data-testid="court-edit-desc" />
            </div>
            <div className="space-y-2">
              <Label className="label-text">{L('Court Type', 'Platztyp')}</Label>
              <Select value={courtEditForm.is_indoor ? 'indoor' : 'outdoor'} onValueChange={(v) => setCourtEditForm({...courtEditForm, is_indoor: v === 'indoor'})}>
                <SelectTrigger className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="indoor">Indoor</SelectItem>
                  <SelectItem value="outdoor">Outdoor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setEditingCourt(null)} className="flex-1 border-slate-200 text-slate-600">{L('Cancel', 'Abbrechen')}</Button>
              <Button onClick={saveCourtEdit} disabled={!courtEditForm.name}
                className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold" data-testid="save-court-edit">
                {L('Save', 'Speichern')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PRICE REPAIR DIALOG */}
      <Dialog open={showRepair} onOpenChange={setShowRepair}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="repair-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{L('Repair booking prices', 'Buchungspreise reparieren')}</DialogTitle>
            <DialogDescription>
              {L(
                'Recompute every non-cancelled booking against the current pricing (58 / 68 / 68 CHF/h). Old recurring bookings created before the price-fix may have wrong amounts.',
                'Berechnet jede nicht-stornierte Buchung mit den aktuellen Preisen neu (58 / 68 / 68 CHF/h). Alte Fixstunden, die vor dem Bugfix erstellt wurden, können falsche Beträge haben.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <input
                type="checkbox"
                id="repair-include-paid"
                checked={repairIncludePaid}
                onChange={(e) => { setRepairIncludePaid(e.target.checked); loadAudit(e.target.checked); }}
                data-testid="repair-include-paid"
              />
              <Label htmlFor="repair-include-paid" className="text-sm font-medium text-amber-900 cursor-pointer">
                {L('Also update bookings that are already PAID (rewrites finance history)', 'Auch bereits BEZAHLTE Buchungen aktualisieren (Finanzhistorie überschreiben)')}
              </Label>
            </div>

            {repairLoading && (
              <div className="text-center py-6 text-slate-500 text-sm">{L('Loading…', 'Lädt…')}</div>
            )}

            {!repairLoading && repairAudit && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                    <div className="text-[11px] uppercase font-bold text-slate-500">{L('Scanned', 'Geprüft')}</div>
                    <div className="text-2xl font-extrabold text-slate-900">{repairAudit.scanned}</div>
                  </div>
                  <div className={`rounded-lg border p-3 ${repairAudit.mismatches > 0 ? 'border-rose-200 bg-rose-50' : 'border-emerald-200 bg-emerald-50'}`}>
                    <div className={`text-[11px] uppercase font-bold ${repairAudit.mismatches > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{L('Wrong', 'Falsch')}</div>
                    <div className={`text-2xl font-extrabold ${repairAudit.mismatches > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{repairAudit.mismatches}</div>
                  </div>
                  <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                    <div className="text-[11px] uppercase font-bold text-slate-500">{L('Revenue diff', 'Umsatz-Diff')}</div>
                    <div className={`text-2xl font-extrabold ${repairAudit.total_revenue_diff >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {repairAudit.total_revenue_diff >= 0 ? '+' : ''}{repairAudit.total_revenue_diff} CHF
                    </div>
                  </div>
                </div>

                {repairAudit.issues && repairAudit.issues.length > 0 && (
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <div className="px-3 py-2 bg-slate-50 text-[11px] uppercase font-bold text-slate-600 border-b border-slate-200">
                      {L('First mismatches', 'Erste Abweichungen')}
                    </div>
                    <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 text-sm">
                      {repairAudit.issues.slice(0, 30).map((it, idx) => (
                        <div key={idx} className="px-3 py-1.5 flex items-center gap-2">
                          <div className="flex-1 min-w-0 truncate text-slate-700">
                            {it.date} {it.start_time} · {it.court_name} · {it.duration_minutes}min
                            {it.payment_status === 'paid' && <span className="ml-2 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 rounded">PAID</span>}
                          </div>
                          <div className="text-rose-600 font-semibold tabular-nums">{it.current_amount} →</div>
                          <div className="text-emerald-700 font-bold tabular-nums">{it.expected_amount} CHF</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {repairAudit.mismatches === 0 && (
                  <div className="text-center py-4 text-emerald-700 font-semibold">
                    ✓ {L('All prices are correct!', 'Alle Preise sind korrekt!')}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowRepair(false)} className="flex-1 border-slate-200 text-slate-600" data-testid="repair-cancel">
              {L('Close', 'Schliessen')}
            </Button>
            <Button
              onClick={runRepair}
              disabled={repairLoading || !repairAudit || repairAudit.mismatches === 0}
              className="flex-1 bg-rose-500 text-white hover:bg-rose-600 font-bold disabled:opacity-40"
              data-testid="repair-confirm"
            >
              <Wrench size={16} className="mr-2" />
              {repairLoading ? L('Working…', 'Arbeitet…') : L(`Repair ${repairAudit?.mismatches || 0} bookings`, `${repairAudit?.mismatches || 0} Buchungen reparieren`)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBookings;
